import { useMutation, useQuery, useQueryClient, QueryObserverResult, QueryKey, QueryOptions } from 'react-query';
import { defaultDeviceTemplateId, Device } from 'web/model/Device';
import { DeviceData } from 'web/model/DeviceData';
import { DeviceLambdaSetting } from 'web/model/DeviceLambdaSetting';
import { DisplayedChannel } from 'web/model/DisplayedChannel';
import { DeviceTemplate } from 'web/model/DeviceTemplate';
import { DeviceTemplateLambdaTrigger } from 'web/model/DeviceTemplateLambdaTrigger';
import { L1Measurement, L1MeasurementType } from 'web/model/L1Measurement';
import { Label, LabelType } from 'web/model/Label';
import { LabelSet, LabelSetType } from 'web/model/LabelSet';
import { LambdaTrigger } from 'web/model/LambdaTrigger';
import { ManualMeasurement } from 'web/model/ManualMeasurement';
import { Objective } from 'web/model/Objective';
import { Organization } from 'web/model/Organization';
import {
    BeginOrganizationAiChatMessageResponse,
    OrganizationAiChatThreadDetail,
    OrganizationAiChatThreadSummary,
    SendOrganizationAiChatMessageResponse,
} from 'web/model/OrganizationAiChat';
import { OrganizationInvitation } from 'web/model/OrganizationInvitation';
import { Patient } from 'web/model/Patient';
import { PatientNote } from 'web/model/PatientNote';
import { User } from 'web/model/User';
import { BillingPlan } from 'web/model/BillingPlan';
import { FirmwareVersion, NxhubFirmwareCommandsRequest, NxhubFirmwareCommandsResponse } from 'web/model/FirmwareVersion';
import { TemplateType } from 'web/constants/medicalTemplateTypes';
import {
    sendDelete,
    sendGet,
    sendGetBlob,
    sendPatch,
    sendPatchForJson,
    sendPost,
    sendPostForJson,
    sendPutForm,
    sendPutForJson,
    addOptionalQueryParam,
    appendQueryParams,
    sendPostFormForJson,
    sendPostForm,
    sendGetWithTotal,
    sendGetWithoutJson,
    sendPostFormWithFilesForJson,
} from 'web/utils/request';
import { PatientNoteType } from 'web/model/PatientNoteType';
import { Entites } from 'web/model/Entites';
import { OrganizationStatistics } from 'web/model/OrganizationStatistic';
import { MedicalTemplate } from 'web/model/MedicalTemplate';
import { L1MeasurementRequest } from 'web/model/L1MeasurementRequest';
import { Language } from 'web/model/Language';
import { DetailedUser } from 'web/model/DetailedUser';
import { Referral } from 'web/model/Referral';
import { useCurrentUser } from 'web/hooks/useCurrentUser';
import JSZip from 'jszip';
import { NxScopeLog, NxScopeLogResponse } from 'web/types/NxScopeLog';
import { collectDeviceMetadata, DeviceMetadata } from 'web/utils/DeviceMetadataUtil';

// export const queryClient = new QueryClient();

function createUseQuery<Params extends any[], Result>(queryKey: QueryKey, func: (...params: Params) => Promise<Result>, options?: QueryOptions<Result, unknown, Result, QueryKey>) {
    const query = (...params: Params) => {
        return useQuery<Result>([queryKey, ...params], () => func(...params), options);
    }
    query.func = () => {
        const queryClient = useQueryClient();
        return (...params: Params) => {
            return queryClient.fetchQuery<Result>([queryKey, ...params], () => func(...params));
        };
    }
    return query;
}

export const useGetBillingDashboard = () => {
    return useQuery<BillingPlan[]>('billing/dashboard', async () => {
        const reply: BillingPlan[] = await sendGet('/api/billing/dashboard');
        return reply.map(r => new BillingPlan(r));
    }, {
        retry: false,
    });
}

export type GetOrganizationsParams = {
    name?: string | undefined;
}

export const useGetOrganizations = (params?: GetOrganizationsParams, offset?: number, limit?: number) => {
    return useQuery<{data: Organization[], total: number}>(['organizations', params, offset, limit], async () => {
        let url = '/api/organizations';
        if (offset !== undefined && limit !== undefined) {
            url = appendQueryParams(url, { offset, limit });
        }
        if (params?.name) {
            url = appendQueryParams(url, { NameContains: params.name });
        }
        const [reply, total] = await sendGetWithTotal<Organization[]>(url);
        return {
            data: reply.map(d => new Organization(d)),
            total
        };
    });
}

export enum DeviceType {
    Android,
    iOS,
    Web,
}

export const useGetOrganizationAdminStatistics = createUseQuery('organization-admin-statistics', async () => {
    const reply = await sendGet(`/api/organizations/admin-statistics`);
    return reply as {
        totalUserCount: number;
        totalOrgCount: number;
        totalOrgInForceSubscriptionCount: number;
        labelTypeCounts: Record<string, number>;
        perDeviceType: Record<keyof typeof DeviceType, Record<string, number>>;
        perOrg: Record<string, {
            totalUserCount: number;
            labelTypeCounts: Record<string, number>,
            deviceTypeCounts: Record<keyof typeof DeviceType, Record<string, number>>,
        }>,
    };
});

export const useGetOrganization = createUseQuery('organizations', async (id: string | undefined) => {
    if (!id) return null;

    const reply: Organization = await sendGet(`/api/organizations/${id}`);
    return new Organization(reply);
});

export const useGetOrganizationPreferences = createUseQuery('organizations/prefernces', async (id?: string) => {
    if (!id) {
        return null;
    }
    const reply: Organization = await sendGet(`/api/organizations/${id}/preferences`);
    return new Organization(reply);
});

export type ListOrganizationAiChatThreadsParams = {
    offset?: number;
    limit?: number;
    isArchived?: boolean;
};

export const useGetOrganizationAiChatThreads = createUseQuery(
    'organization-ai-chat-threads',
    async (organizationId: string | undefined, params?: ListOrganizationAiChatThreadsParams) => {
        if (!organizationId) return { data: [], total: 0 };

        const url = appendQueryParams(`/api/organizations/${organizationId}/ai-chat/threads`, {
            isArchived: params?.isArchived,
            offset: params?.offset ?? 0,
            limit: params?.limit ?? 50,
        });
        const [reply, total] = await sendGetWithTotal<OrganizationAiChatThreadSummary[]>(url);
        return {
            data: reply,
            total,
        };
    }
);

export const useGetOrganizationAiChatThread = createUseQuery(
    'organization-ai-chat-thread',
    async (organizationId: string | undefined, threadId: string | undefined) => {
        if (!organizationId || !threadId) return null;

        return await sendGet<OrganizationAiChatThreadDetail>(
            `/api/organizations/${organizationId}/ai-chat/threads/${threadId}`
        );
    }
);

export const useCreateOrganizationAiChatThread = () => {
    const queryClient = useQueryClient();
    return useMutation<OrganizationAiChatThreadDetail, Error, { organizationId: string, title?: string }>(
        async ({ organizationId, title }) => {
            return await sendPostForJson(`/api/organizations/${organizationId}/ai-chat/threads`, { title });
        },
        {
            onSuccess: (_thread, variables) => {
                queryClient.invalidateQueries(['organization-ai-chat-threads', variables.organizationId]);
            },
        }
    );
};

export const useDeleteOrganizationAiChatThread = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { organizationId: string, threadId: string }>(
        async ({ organizationId, threadId }) => {
            await sendDelete(`/api/organizations/${organizationId}/ai-chat/threads/${threadId}`);
        },
        {
            onSuccess: (_result, variables) => {
                queryClient.invalidateQueries(['organization-ai-chat-threads', variables.organizationId]);
                queryClient.invalidateQueries(['organization-ai-chat-thread', variables.organizationId, variables.threadId]);
            },
        }
    );
};

// Clears the user's entire chat history for an org. Drops every cached
// thread-detail for the org (removeQueries, not invalidate — the threads no
// longer exist) and refreshes the history list.
export const useDeleteAllOrganizationAiChatThreads = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { organizationId: string }>(
        async ({ organizationId }) => {
            await sendDelete(`/api/organizations/${organizationId}/ai-chat/threads`);
        },
        {
            onSuccess: (_result, variables) => {
                queryClient.removeQueries(['organization-ai-chat-thread', variables.organizationId]);
                queryClient.invalidateQueries(['organization-ai-chat-threads', variables.organizationId]);
            },
        }
    );
};

// The browser's UTC offset in minutes, positive east of UTC (getTimezoneOffset is
// inverted). Sent with each chat message so the backend answers "today"/"this week"
// and presents times in the user's local time instead of UTC.
const userUtcOffsetMinutes = () => -new Date().getTimezoneOffset();

export const useSendOrganizationAiChatMessage = () => {
    const queryClient = useQueryClient();
    return useMutation<SendOrganizationAiChatMessageResponse, Error, { organizationId: string, threadId: string, content: string }>(
        async ({ organizationId, threadId, content }) => {
            return await sendPostForJson(
                `/api/organizations/${organizationId}/ai-chat/threads/${threadId}/messages`,
                { content, utcOffsetMinutes: userUtcOffsetMinutes() }
            );
        },
        {
            onSuccess: (_response, variables) => {
                queryClient.invalidateQueries(['organization-ai-chat-threads', variables.organizationId]);
                queryClient.invalidateQueries(['organization-ai-chat-thread', variables.organizationId, variables.threadId]);
            },
        }
    );
};

// Persists the user's message and returns its id so the caller can open the
// SSE reply stream. Pair with useOrganizationAiChatStream. Does not invalidate
// the thread cache here — the stream's "done" event triggers that once the
// assistant reply lands.
export const useBeginOrganizationAiChatMessage = () => {
    return useMutation<BeginOrganizationAiChatMessageResponse, Error, { organizationId: string, threadId: string, content: string, scopeConversationId?: string }>(
        async ({ organizationId, threadId, content, scopeConversationId }) => {
            return await sendPostForJson(
                `/api/organizations/${organizationId}/ai-chat/threads/${threadId}/messages/begin`,
                { content, scopeConversationId, utcOffsetMinutes: userUtcOffsetMinutes() }
            );
        }
    );
};

export const useGetUser = createUseQuery('users', async (id?: string) => {
    if (!id) return null;

    const reply = await sendGet<DetailedUser>(`/api/users/${id}`);
    return new DetailedUser(reply);
});

export type GetUsersParams = {
    id?: string;
    name?: string;
    email?: string;
    organizationId?: string;
}

export const useGetUsers = createUseQuery('search-users', async (params?: GetUsersParams, offset = 0, limit = 50) => {
    let url = appendQueryParams(`/api/users`, {
        ...params,
        offset,
        limit,
    });
    const [reply, total] = await sendGetWithTotal<User[]>(url);
    return {
        data: reply.map(d => new User(d)),
        total,
    };
});

export const useGetReferrals = createUseQuery('referrals', async (params?: {}, offset = 0, limit = 50) => {
    let url = appendQueryParams(`/api/referrals`, {
        ...params,
        offset,
        limit,
    });
    const [reply, total] = await sendGetWithTotal<Referral[]>(url);
    return {
        data: reply.map(d => new Referral(d)),
        total,
    };
});

export type GetModifiedPatientNotesParams = {
    organizationId?: string;
    type?: PatientNoteType;
}

export const useGetModifiedPatientNotes = createUseQuery(
    'modified-patient-notes',
    async (params?: GetModifiedPatientNotesParams, offset = 0, limit = 50) => {
        let url = appendQueryParams(`/api/modified-patient-notes`, {
            ...params,
            offset,
            limit,
        });
        const [reply, total] = await sendGetWithTotal<PatientNote[]>(url);
        return {
            data: reply.map(d => new PatientNote(d)),
            total,
        };
    }
);

export const useAddAudioRecordingDevice = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async (serial: string) => {
            const request = {
                serial: serial,
                deviceTemplateId: defaultDeviceTemplateId,
                displayedChannels: [
                    new DisplayedChannel({
                        channelContent: "AudioRecording"
                    })
                ],
                metadata: collectDeviceMetadata(),
            };
            const device = await sendPostForJson('/api/devices', request);
            return device.id;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['devices']);
            }
        }
    );
};

export const useUpdateDeviceMetadata = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ deviceSerial, metadata }: { deviceSerial: string, metadata: DeviceMetadata }) => {
            const request = { metadata };
            const device = await sendPatchForJson(`/api/devices/${deviceSerial}`, request);
            return new Device(device);
        },
        {
            onSuccess: (device) => {
                queryClient.invalidateQueries(['device', device.id]);
            }
        }
    );
};

export const useAddNxDevice = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async (serial: string) => {
            const request = {
                serial: serial,
                deviceTemplateId: defaultDeviceTemplateId,
                displayedChannels: [
                    new DisplayedChannel({ channelContent: "AccelerometerX" }),
                    new DisplayedChannel({ channelContent: "AccelerometerY" }),
                    new DisplayedChannel({ channelContent: "AccelerometerZ" }),
                    new DisplayedChannel({ channelContent: "GyroX" }),
                    new DisplayedChannel({ channelContent: "GyroY" }),
                    new DisplayedChannel({ channelContent: "GyroZ" }),
                    new DisplayedChannel({ channelContent: "MagnetometerX" }),
                    new DisplayedChannel({ channelContent: "MagnetometerY" }),
                    new DisplayedChannel({ channelContent: "MagnetometerZ" }),
                    new DisplayedChannel({ channelContent: "VoltageBattery" }),
                    new DisplayedChannel({ channelContent: "TemperatureObject" }),
                    new DisplayedChannel({ channelContent: "TemperatureAmbient" }),
                    new DisplayedChannel({ channelContent: "Temperature" }),
                    new DisplayedChannel({ channelContent: "Audio" }),
                    new DisplayedChannel({ channelContent: "AudioVolume" }),
                    new DisplayedChannel({ channelContent: "Stethoscope" }),
                    new DisplayedChannel({ channelContent: "StethoscopeRef" }),
                    new DisplayedChannel({ channelContent: "StethoscopeANC" }),
                    new DisplayedChannel({ channelContent: "Ecg0" }),
                    new DisplayedChannel({ channelContent: "Ecg1" }),
                    new DisplayedChannel({ channelContent: "AudioRecording" }),
                    new DisplayedChannel({ channelContent: "CompressedAudio" }),
                ],
            };
            const device = await sendPostForJson('/api/devices', request);
            return device.id;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['devices']);
            }
        }
    );
};

export const useValidateDeviceSerial = (deviceId: string, serial: string) => {
    return useQuery(
        ['validateDeviceSerial', deviceId, serial],
        async () => {
            try {
                const device = await sendGet(`/api/devices/${deviceId}`);
                if (device.serial === serial) {
                    return true;
                }
            } catch (error) {
                console.error("Error ensuring device exists:", error);
            }
            return false;
        },
        {
            refetchOnWindowFocus: false,
            cacheTime: 1000 * 60 * 60 * 24, // 24 hours
            enabled: !!deviceId && !!serial, // Only run the query if both deviceId and serial are provided
        }
    );
};

export const useGetDeviceTemplate = (id: string) => {
    return useQuery<DeviceTemplate>(['device-templates', id], async () => {
        const reply: DeviceTemplate = await sendGet(`/api/device-templates/${id}`);
        return new DeviceTemplate(reply);
    });
}

export const useGetDeviceTemplates = () => {
    return useQuery<DeviceTemplate[]>('device-templates', async () => {
        const reply: DeviceTemplate[] = await sendGet('/api/device-templates');
        return reply.map(d => new DeviceTemplate(d))
    });
}

export const useGetDeviceTemplatesFunc = () => {
    const queryClient = useQueryClient();
    return () => queryClient.fetchQuery<DeviceTemplate[]>('device-templates', async () => {
        const reply: DeviceTemplate[] = await sendGet(`/api/device-templates`);
        return reply.map(d => new DeviceTemplate(d));
    });
}

export const useGetDevice = (deviceId: string) => {
    return useQuery<Device>(['devices', deviceId], async () => {
        const reply: Device = await sendGet(`/api/devices/${deviceId}`);
        return new Device(reply);
    }, {
        cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    });
}

export const useGetDevices = (deviceSerial?: string | string[]) => {
    return useQuery<Device[]>(['devices', deviceSerial], async () => {
        let url = '/api/devices?limit=1000';
        if (deviceSerial) {
            if (Array.isArray(deviceSerial)) {
                // Don't add filter if empty array
                if (deviceSerial.length > 0) {
                    url += `&deviceSerial=${deviceSerial.join(',')}`;
                }
            } else {
                url += `&deviceSerial=${deviceSerial}`;
            }
        }
        const reply: Device[] = await sendGet(url);
        return reply.map(d => new Device(d))
    }, {
        // Only enable the query if we have device serials to look for, or if we explicitly want all devices
        enabled: !Array.isArray(deviceSerial) || deviceSerial.length > 0 || deviceSerial === undefined
    });
}

export const useGetLanguages = () => {
    return useQuery<Language[]>('languages', async () => {
        const reply: Language[] = await sendGet(`/api/system/languages`);
        return reply.map(d => new Language(d));
    }, {
        cacheTime: 1000 * 60 * 60 * 24, // 24 hours
        refetchOnWindowFocus: false,
    });
}

export const useRefetchableLabel = (labelId?: string): QueryObserverResult<Label> => {
    return useQuery<Label>(['label', labelId], async () => {
        if (!labelId) throw new Error('labelId is required');
        const reply: Label = await sendGet(`/api/labels/${labelId}`);
        return new Label(reply);
    });
};

export const useGetOrganizationInvitationsToMe = () => {
    return useQuery<OrganizationInvitation[]>('organization-invitations-to-me', async () => {
        const reply: OrganizationInvitation[] = await sendGet(`/api/users/organization-invitations`);
        return reply.map(d => new OrganizationInvitation(d));
    });
}

export const useGetOrganizationInvitations = () => {
    return useQuery<OrganizationInvitation[]>('organization-invitations', async () => {
        const reply: OrganizationInvitation[] = await sendGet(`/api/organizations/invitations`);
        return reply.map(d => new OrganizationInvitation(d));
    });
}

export const useGetOrganizationInvitationsFunc = () => {
    const queryClient = useQueryClient();
    return () => queryClient.fetchQuery<OrganizationInvitation[]>('organization-invitations', async () => {
        const reply: OrganizationInvitation[] = await sendGet(`/api/organizations/invitations`);
        return reply.map(d => new OrganizationInvitation(d));
    });
}

export interface GetLabelsParams {
    type?: LabelType | null | undefined;
    labelSetType?: LabelSetType | null | undefined;
    offset?: number;
    limit?: number;
    patientName?: string;
    deviceSerial?: string;
    userId?: string;
}

export const useGetLabels = createUseQuery('labels', async (params?: GetLabelsParams) => {
    const url = appendQueryParams(`/api/labels`, {
        type: params?.type,
        labelSetType: params?.labelSetType,
        patientName: params?.patientName,
        deviceSerial: params?.deviceSerial,
        offset: params?.offset,
        limit: params?.limit,
        userId: params?.userId,
    });
    const reply = await sendGetWithTotal<Label[]>(url);
    reply[0] = reply[0].map(d => new Label(d));
    return reply;
});

export const useGetLabel = (labelId?: string) => {
    // Always call useQuery so React's hook order stays stable across
    // renders — callers commonly pass an id that arrives one render
    // later (e.g. labelId = parentQuery.data?.someId), and returning
    // early before the hook on the first render would crash the second.
    return useQuery<Label | null>(
        ['label', labelId ?? 'none'],
        async () => {
            if (!labelId) return null;
            const reply: Label = await sendGet(`/api/labels/${labelId}`);
            return new Label(reply);
        },
        { enabled: !!labelId },
    );
}


export const useGetLabelDataFunc = () => {
    return async (
        labelId: string,
        fromTime: Date,
        toTime: Date,
        subsamplingPeriodSec: number | null,
        signal?: AbortSignal
    ): Promise<DeviceData> => {
        const url = appendQueryParams(
            `/api/labels/${labelId}/data`, {
                fromTime: fromTime.getTime(),
                toTime: toTime?.getTime(),
                subsamplingPeriodSec,
            },
        );
        const reply: DeviceData = await sendGet(url, signal);
        return new DeviceData(reply);
    };
}

export const useAddLabelIntake = () => {
    return useMutation<{ id: string }, Error, { deviceSerial: string, label: Label }>(
        async ({ deviceSerial, label }) => {
            const response = await sendPostForJson(
                `/api/devices/${deviceSerial}/label-sets/device/labels`,
                {
                    name: label.name,
                    fromTime: label.fromTime,
                    toTime: label.toTime,
                    color: label.color,
                    description: label.description,
                    patientId: label.patient?.id ?? label.patientId,
                    type: label.type,
                    medicalTemplateId: label.medicalTemplateId,
                    metadata: typeof label.metadata === 'object' ? JSON.stringify(label.metadata) : label.metadata,
                    isCreatedByOnboarding: label.isCreatedByOnboarding,
                }
            );
            return response;
        }
    );
};

export const useGetLambdaTriggers = (deviceId: string) => {
    return useQuery<LambdaTrigger[]>(['lambdaTriggers', deviceId], async () => {
        const reply: LambdaTrigger[] = await sendGet(`/api/devices/${deviceId}/lambda-triggers`);
        return reply.map(d => new LambdaTrigger(d));
    });
}

export const useGetPatient = createUseQuery<[string | undefined], Patient | null>('patients', async (id: string | undefined) => {
    if (id === undefined) {
        return null;
    }
    const reply: Patient = await sendGet(`/api/patients/${id}`);
    return new Patient(reply);
});

interface PcgSegmentation {
    analysisPlots: string;
    aiSummary: string;
}

export const useGetPcgSegmentationPlots = (id: string = "", overwrite?: boolean, options?: { enabled?: boolean, refetchOnWindowFocus?: boolean }) => {
    return useQuery<PcgSegmentation, Error>(
        ['pcg-segmentation-plots', id], 
        async () => {
            let url = `/api/labels/${id}/pcg-segmentation-plots`;
            url = addOptionalQueryParam(url, 'overwrite', overwrite);
            const response: PcgSegmentation = await sendGet(url);
            return response;
        }, 
        {
            retry: false,
            enabled: options?.enabled ?? true, // Default to true if not specified
            refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false, // Default to false if not specified
        }
    );
};

export type GetPatientsParams = {
    generalSearch?: string;
    name?: string;
    ownerLastName?: string;
    caregiver?: string;
    breed?: string;
    from?: Date;
    to?: Date;
    organizationId?: string;
}
export const useGetPatients = (params?: GetPatientsParams, offset = 0, limit = 50) => {
    return useQuery<Entites<Patient>>(['search-patients', JSON.stringify(params), offset, limit], async () => {
        let url = `/api/patients`;
        url = appendQueryParams(url, { ...params, offset, limit });
        const [reply, total] = await sendGetWithTotal<Patient[]>(url);
        return {
            data: reply.map(d => new Patient(d)),
            total,
        };
    });
}

export const useGetLabelSetsForDevice = (deviceSerial: string) => {
    return useQuery<LabelSet[]>(['labelSets', deviceSerial], async () => {
        const response: LabelSet[] = await sendGet(`/api/devices/${deviceSerial}/label-sets`);
        return response.map(l => new LabelSet(l));
    });
}

export const useOrganizationStatistics = createUseQuery('organization-statistics', async () => {
    const response = await sendGet<OrganizationStatistics>('/api/organizations/statistics');

    Object.keys(response).forEach(key => {
        response[key].user = new User(response[key].user);
    });

    return response;
});

export const getLabelSet = (deviceSerial: string, labelSetId: string) => {
    return useQuery<LabelSet>(['labelSet', labelSetId], async () => {
        const response: LabelSet = await sendGet(`/api/devices/${deviceSerial}/label-sets/${labelSetId}`);
        return new LabelSet(response);
    });
}

export const useListObjectives = (patientId: string) => {
    return useQuery<Objective[]>(['objectives', patientId], async () => {
        if (patientId === '') return [];

        const response: Objective[] = await sendGet(`/api/patients/${patientId}/objectives`);
        return response.map(r => new Objective(r));
    });
}

export const useListDeviceLambdaSettingFunc = () => {
    const queryClient = useQueryClient();
    return () => queryClient.fetchQuery<DeviceLambdaSetting[]>(['device-lambda-setting'], async () => {
        let url = `/api/devices/lambda-setting`
        const response: DeviceLambdaSetting[] = await sendGet(url)
        return response.map(r => new DeviceLambdaSetting(r));
    });
}

export const useAddLabelSet = () => {
    return useMutation<LabelSet, Error, { deviceSerial: string, labelSet: LabelSet }>(
        async ({ deviceSerial, labelSet }) => {
            const response: LabelSet = await sendPostForJson(
                `/api/devices/${deviceSerial}/label-sets`,
                {
                    name: labelSet.name,
                    labels: labelSet.labels.map(label => {
                        const { id: _, ...newLabel } = label
                        return newLabel
                    }),
                    userId: labelSet.userId
                }
            );
            return new LabelSet(response);
        }
    );
}

export const useAddOrganization = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, Partial<Organization>>(
        async (org) => {
            await sendPost(
                `/api/organizations`,
                {
                    name: org.name,
                }
            );

            queryClient.invalidateQueries(['organizations']);
        }
    );
}

export const useAdminUpdateUser = () => {
    return useMutation<void, Error, Partial<DetailedUser>>(
        async (user) => {
            await sendPatch(
                `/api/users/${user.id}`,
                {
                    email: user.email,
                    firstName: user.firstName,
                    middleNames: user.middleNames,
                    lastName: user.lastName,
                    organizations: user.organizations?.map(o => o.id),
                    soapNoteTemplate: user.soapNoteTemplate,
                    isShowingFirstTimeTutorial: user.isShowingFirstTimeTutorial,
                    lockoutEnabled: user.lockoutEnabled,
                    lockoutEnd: user.lockoutEnd,
                    isEmailConfirmed: user.isEmailConfirmed,
                    isForceChangePassword: user.isForceChangePassword,
                    newPassword: user.newPassword,
                }
            );
        }
    );
}

export const useUpdateOrganization = () => {
    return useMutation<void, Error, Partial<Organization>>(
        async (org) => {
            await sendPatch(
                `/api/organizations/${org.id}`,
                {
                    name: org.name,
                    SOAPNoteRecipientEmail: org.soapNoteRecipientEmail,
                    nxVetForceSubscriptionEndDate: org.nxVetForceSubscriptionEndDate,
                    nxCallForceSubscriptionEndDate: org.nxCallForceSubscriptionEndDate,

                    reportIncludeCaseOwnerName: org.reportIncludeCaseOwnerName,
                    reportIncludeLogo: org.reportIncludeLogo,
                    reportIncludeHospitalName: org.reportIncludeHospitalName,
                    reportCustomHeaderText: org.reportCustomHeaderText,
                    reportIncludeHospitalInfo: org.reportIncludeHospitalInfo,
                    reportIncludePhoneNumber: org.reportIncludePhoneNumber,
                    reportIncludeEmail: org.reportIncludeEmail,
                    reportIncludePageNumber: org.reportIncludePageNumber,
                    reportIncludeDate: org.reportIncludeDate,
                }
            );
        }
    );
}

export const useUpdateOrganizationOwners = () => {
    return useMutation<void, Error, { organizationId: string, owners: string[] }>(
        async ({ organizationId, owners }) => {
            await sendPost(
                `/api/organizations/${organizationId}/owners`,
                {
                    owners,
                }
            );
        }
    );
}

export const useRemoveUserFromOrganization = () => {
    return useMutation<void, Error, { user: User, organization: Organization }>(
        async ({ user, organization }) => {
            await sendDelete(`/api/organizations/${organization.id}/users/${user.id}`);
        }
    );
}

export const useRemoveUserFromCurrentOrganization = () => {
    return useMutation<void, Error, { user: User }>(
        async ({ user }) => {
            await sendDelete(`/api/organizations/current/users/${user.id}`);
        }
    );
}

export const useCopyLabelToAdmin = () => {
    return useMutation<Label, Error, string>(
        async (labelId) => {
            const result: Label = await sendPostForJson(`/api/labels/${labelId}/copy-to-admin`);
            return new Label(result);
        }
    );
}

export const useDeleteLabel = () => {
    return useMutation<void, Error, string>(
        async (labelId) => {
            await sendDelete(`/api/labels/${labelId}`);
        }
    );
}

export const useUpdateOrganizationPreferences = () => {
    return useMutation<void, Error, Partial<Organization & {organizationLogo: File, sampleMedicalRecord: File}>>(
        async (org) => {
            await sendPostForm(
                `/api/organizations/${org.id}`,
                {
                    name: org.name,
                    primaryLanguage: org.primaryLanguage,
                    preferredMedicalRecordLanguage: org.preferredMedicalRecordLanguage,
                    patientManagementSoftware: org.patientManagementSoftware,
                    otherPatientMangaugeSoftware: org.otherPatientMangaugeSoftware,
                    isEnablingOnMedicalRecordCompleteNotification: org.isEnablingOnMedicalRecordCompleteNotification,
                    isRequiringRecordingConsent: org.isRequiringRecordingConsent,
                    isShowingCompletePreferences: org.isShowingCompletePreferences,
                    organizationLogo: org.organizationLogo,
                    sampleMedicalRecord: org.sampleMedicalRecord,
                    address: org.address,
                    country: org.country,
                    publicEmail: org.publicEmail,
                    phoneNumber: org.phoneNumber,
                    copyPasteFormat: org.copyPasteFormat,
                }
            );
        }
    );
}

export const useUpdateSOAPNoteRecipientEmail = () => {
    return useMutation<void, Error, { id: string, soapNoteRecipientEmail: string }>(
        async ({ id, soapNoteRecipientEmail }) => {
            await sendPatch(`/api/organizations/${id}/soapnote-recipient-email`, {
                soapNoteRecipientEmail: soapNoteRecipientEmail,
                isShowingCompletePreferences: false,
            });
        }
    );
};

export const useAddLambdaTrigger = () => {
    return useMutation<LambdaTrigger, Error, LambdaTrigger>(
        async (lambdaTrigger) => {
            const response: LabelSet = await sendPostForJson(
                `/api/devices/${lambdaTrigger.deviceId}/lambda-triggers`,
                {
                    deviceId: lambdaTrigger.deviceId,
                    labelType: lambdaTrigger.labelType,
                    lambdaUrl: lambdaTrigger.lambdaUrl,
                    isOverriding: lambdaTrigger.isOverriding,
                }
            );
            return new LambdaTrigger(response);
        }
    );
}

export const useUpsertDeviceTemplate = () => {
    return useMutation<DeviceTemplate, Error, DeviceTemplate>(
        async (template) => {
            const response: LabelSet = template.id ? (
                await sendPatchForJson(
                    `/api/device-templates/${template.id}`,
                    {
                        name: template.name,
                        triggers: template.triggers,
                    },
                )
            ) : (
                await sendPostForJson(
                    `/api/device-templates/`,
                    {
                        name: template.name,
                        triggers: template.triggers.map(t => ({
                            labelType: t.labelType,
                            lambdaUrl: t.lambdaUrl,
                        })),
                    },
                )
            );
            return new DeviceTemplate(response);
        }
    );
}

export const useAddDeviceTemplate = () => {
    return useMutation<DeviceTemplate, Error, DeviceTemplate>(
        async (template) => {
            const response: LabelSet = await sendPostForJson(
                `/api/device-templates/`,
                {
                    name: template.name,
                    triggers: template.triggers,
                },
            );
            return new DeviceTemplate(response);
        }
    );
}

export const useUpdateDeviceTemplate = () => {
    return useMutation<DeviceTemplate, Error, DeviceTemplate>(
        async (template) => {
            const response: LabelSet = await sendPatchForJson(
                `/api/device-templates/${template.id}`,
                {
                    name: template.name,
                    triggers: template.triggers,
                },
            );
            return new DeviceTemplate(response);
        }
    );
}

export const useUpdatePatient = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, Patient | { patient: Patient, file: File | null }>(
        async (param) => {
            const patient = param instanceof Patient ? param : param.patient;

            await sendPatch(
                `/api/patients/${patient.id}`,
                {
                    name: patient.name,
                    ownerLastName: patient.ownerLastName,
                    dateOfBirth: patient.dateOfBirth,
                    breed: patient.breed,
                    sex: patient.sex,
                    caregiverId: patient.caregiver?.id,
                    phoneNumber: patient.phoneNumber,
                    sterilizationStatus: patient.sterilizationStatus
                }
            );

            if (!(param instanceof Patient)) {
                if (param.file) {
                    await sendPutForm(`/api/patients/${patient.id}/profile-picture`, { file: param.file });
                } else {
                    await sendDelete(`/api/patients/${patient.id}/profile-picture`);
                }
            }

            queryClient.invalidateQueries(['patients', patient.id]);
            return;
        }
    );
}

export const useCreateUser = () => {
    return useMutation<User, Error, {
        email: string;
        firstName: string;
        middleName?: string;
        lastName: string;
        newOrganizationName?: string;
    }>(
        async (user) => {
            const response: User = await sendPostForJson(`/api/users`, {
                ...user,
            });
            return new User(response);
        }
    );
}

export const useRemoveUser = () => {
    const queryClient = useQueryClient();
    return useMutation<undefined, Error, User>(
        async (user) => {
            await sendDelete(
                `/api/users/${user.id}`
            );
            queryClient.invalidateQueries(['users', user.id]);
            queryClient.invalidateQueries(['search-users']);
        },
    );
}

export const useRemovePatient = () => {
    const queryClient = useQueryClient();
    return useMutation<undefined, Error, Patient>(
        async (patient) => {
            await sendDelete(
                `/api/patients/${patient.id}`
            );
            queryClient.invalidateQueries(['patients', patient.id]);
            queryClient.invalidateQueries(['search-patients']);
        },
    );
}

export const useRemoveLambdaTrigger = () => {
    return useMutation<undefined, Error, LambdaTrigger>(
        async (lambdaTrigger) => {
            await sendDelete(
                `/api/devices/${lambdaTrigger.deviceId}/lambda-triggers/${lambdaTrigger.id}`
            );
        }
    );
}

export const useRemoveDeviceTemplate = () => {
    return useMutation<undefined, Error, DeviceTemplate>(
        async (template) => {
            await sendDelete(
                `/api/device-templates/${template.id}`
            );
        }
    );
}

export const useRemoveDeviceTemplateLambdaTrigger = () => {
    return useMutation<undefined, Error, DeviceTemplateLambdaTrigger>(
        async (trigger) => {
            await sendDelete(
                `/api/device-templates/${trigger.deviceTemplateId}/lambda-triggers/${trigger.id}`
            );
        }
    );
}

export const useUploadProfilePicture = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { patientId: string, file: File }>(async ({ patientId, file }) => {
        await sendPutForm(`/api/patients/${patientId}/profile-picture`, { file });
        queryClient.invalidateQueries(['patients', patientId]);
        console.log('useUploadProfilePicture', 'invalidated', 'patients', patientId);
    });
}

export const useRemoveProfilePicture = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { patientId: string }>(async ({ patientId }) => {
        await sendDelete(`/api/patients/${patientId}/profile-picture`);
        queryClient.invalidateQueries(['patients', patientId]);
        console.log('useRemoveProfilePicture', 'invalidated', 'patients', patientId);
    });
}

export const useAddManualMeasurement = () => {
    return useMutation<ManualMeasurement, Error, ManualMeasurement>(
        async (entity: ManualMeasurement) => {
            entity = new ManualMeasurement(entity);
            entity.validate();
            entity.normalize();
            const response: ManualMeasurement = await sendPostForJson(
                `/api/manual-measurements/`,
                {
                    patientId: entity.patient?.id,
                    isMetricUnits: entity.isMetricUnits,
                    temperature: entity.temperature,
                    heartRate: entity.heartRate,
                    respiratoryRate: entity.respiratoryRate,
                    spO2: entity.spO2,
                    remarks: entity.remarks,
                    time: entity.time?.getTime(),
                    labelId: entity.labelId,
                }
            );
            return new ManualMeasurement(response);
        }
    );
}

export const useUpdateLabelStatus = () => {
    return useMutation<void, Error, { labelId: string; status: string }>(
        async ({ labelId, status }) => {
            await sendPost(`/api/labels/${labelId}/status`, { status });
        }
    );
}

export const useAssignPatientToLabel = () => {
    return useMutation<void, Error, Label>(
        async (entity: Label) => {
            await sendPost(
                `/api/labels/${entity.id}/assign`,
                {
                    patientId: entity.patient!.id,
                }
            );
        }
    );
}

export const useAcceptOrganizationInvitation = () => {
    return useMutation<void, Error, OrganizationInvitation>(
        async (entity: OrganizationInvitation) => {
            await sendPost(
                `/api/organizations/${entity.organization?.id ?? entity.organizationId}/invitations/accept`,
            );
        }
    );
}

export const useResendOrganizationInvitation = () => {
    return useMutation<void, Error, OrganizationInvitation>(
        async (entity: OrganizationInvitation) => {
            await sendPost(
                `/api/organizations/${entity.organization?.id ?? entity.organizationId}/invitations/${entity.id}/resend`,
            );
        }
    );
}

export const useRemoveOrganizationInvitation = () => {
    return useMutation<void, Error, OrganizationInvitation>(
        async (entity: OrganizationInvitation) => {
            await sendDelete(
                `/api/organizations/${entity.organization?.id ?? entity.organizationId}/invitations/${entity.id}`,
            );
        }
    );
}

export const useSendOrganizationInvitation = () => {
    return useMutation<void, Error, OrganizationInvitation>(
        async (entity: OrganizationInvitation) => {
            await sendPost(
                `/api/organizations/${entity.organization?.id ?? entity.organizationId}/invitations`,
                {
                    email: entity.email,
                },
            );
        }
    );
}

export const useAssignPatientToOrganization = () => {
    return useMutation<void, Error, { patient: Patient, organization: Organization }>(
        async ({ patient, organization }) => {
            await sendPatch(
                `/api/patients/${patient.id}`,
                {
                    OrganizationId: organization.id,
                }
            );
        }
    );
}

export interface UserDeleteRequestForm {
    includeOwnedOrganizations: boolean;
    reason: string;
}

export const useCreateUserDeleteRequest = () => {
    return useMutation<void, Error, UserDeleteRequestForm>(
        async (entity: UserDeleteRequestForm) => {
            await sendPost(`/api/users/delete-request`, entity);
        }
    );
}

interface MonitoredPatientInfo {
    patientId: string,
    patient: Patient,
    thresholds: ThresholdsDTO,
    tpr: L1Measurement;
}

export const useGetMonitoredPatients = (organizationId: string) => {
    return useQuery<MonitoredPatientInfo[]>(['monitoredPatients', organizationId], async () => {
        const reply: MonitoredPatientInfo[] = await sendGet(`/api/monitoring-panel/${organizationId}`);
        return reply;
    });
};

export const useGetMonitoredPatientsAtInterval = (organizationId: string) => {
    return useQuery<MonitoredPatientInfo[]>(['monitoredPatients', organizationId], async () => {
        let url = `/api/monitoring-panel`;
        url = addOptionalQueryParam(url, 'organizationId', organizationId);
        const reply: MonitoredPatientInfo[] = await sendGet(url);
        return reply;
    }, {
        refetchInterval: 6000, // Refetch every 6 seconds
    });
};

export const useAddMonitoredPatient = () => {
    return useMutation<void, Error, { organizationId: string, patientId: string }>(
        async ({ organizationId, patientId }) => {
            let url = `/api/monitoring-panel`
            url = addOptionalQueryParam(url, 'organizationId', organizationId);
            url = addOptionalQueryParam(url, 'patientId', patientId);
            const response = await sendPost(url);
            return response;
        }
    );
};

export const useRemoveMonitoredPatient = () => {
    return useMutation<void, Error, { patientId: string, organizationId: string }>(
        async ({ organizationId, patientId }) => {
            let url = `/api/monitoring-panel/${patientId}`;
            url = addOptionalQueryParam(url, 'organizationId', organizationId);
            const response = await sendDelete(url);
            return response;
        }
    );
};

export const useRemoveOrganization = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, Organization>(
        async (entity: Organization) => {
            await sendDelete(
                `/api/organizations/${entity.id}`,
            );
            queryClient.invalidateQueries(['organizations']);
            queryClient.invalidateQueries(['organizations', entity.id]);
        }
    );
}

export const useClearAllMonitoredPatients = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { organizationId: string }>(
        async ({ organizationId }) => {
            let url = `/api/monitoring-panel`;
            url = addOptionalQueryParam(url, 'organizationId', organizationId);
            await sendDelete(url);
            queryClient.invalidateQueries(['monitoredPatients', organizationId]);
        }
    );
};

export interface ThresholdsDTO {
    MaxTemperature: number;
    MinTemperature: number;
    MaxHeartRate: number;
    MinHeartRate: number;
    MaxRespiratoryRate: number;
    MinRespiratoryRate: number;
}

interface UpdateThresholdsParams {
    organizationId: string;
    patientId: string;
    thresholds: ThresholdsDTO;
}

export const useUpdateMonitoredPatientThresholds = () => {
    return useMutation<void, Error, UpdateThresholdsParams>(
        async ({ organizationId, patientId, thresholds }) => {
            let url = `/api/monitoring-panel/${patientId}`;
            url = addOptionalQueryParam(url, 'organizationId', organizationId);
            await sendPatch(url, { Thresholds: thresholds });
        }
    );
};

export const useUpdateDevice = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, Device>(
        async (entity: Device) => {
            entity = new Device(entity);
            await sendPatch(
                `/api/devices/${entity.serial}`,
                {
                    organizationId: entity.organization?.id,
                    deviceTemplateId: entity.template?.id || '00000000-0000-0000-0000-000000000000',
                    displayedChannels: entity.displayedChannels,
                    friendlyName: entity.friendlyName,
                }
            );
            queryClient.invalidateQueries(['devices', entity.id]);
        }
    );
}

export const useUpdateDeviceFriendlyName = () => {
    const queryClient = useQueryClient();

    return useMutation<Device, Error, { deviceSerial: string; friendlyName: string }>(
        async ({ deviceSerial, friendlyName }) => {
            const response = await sendPatchForJson(
                `/api/devices/${deviceSerial}`,
                {
                    friendlyName: friendlyName,
                }
            );
            return new Device(response);
        },
        {
            onSuccess: (device) => {
                // Invalidate queries to refresh device data
                queryClient.invalidateQueries(['devices', device.id]);
                queryClient.invalidateQueries(['devices', device.serial]);
                queryClient.invalidateQueries(['devices']);
            }
        }
    );
}

export const useUpdateManualMeasurement = () => {
    return useMutation<void, Error, ManualMeasurement>(
        async (entity: ManualMeasurement) => {
            entity = new ManualMeasurement(entity);
            entity.validate();
            entity.normalize();
            await sendPatch(
                `/api/manual-measurements/${entity.id}`,
                {
                    isMetricUnits: entity.isMetricUnits,
                    temperature: entity.temperature,
                    heartRate: entity.heartRate,
                    respiratoryRate: entity.respiratoryRate,
                    spO2: entity.spO2,
                    remarks: entity.remarks,
                }
            );
        }
    );
}

export const useUpdateDeviceLambdaSetting = () => {
    return useMutation<void, Error, DeviceLambdaSetting>(
        async (entity: DeviceLambdaSetting) => {
            entity = new DeviceLambdaSetting(entity);
            await sendPatch(
                `/api/devices/${entity.id}/lambda-setting`,
                {
                    isTriggeringLambdaWhenDataUploaded: entity.isTriggeringLambdaWhenDataUploaded,
                    isTriggeringLambdaWhenSessionEnded: entity.isTriggeringLambdaWhenSessionEnded,
                    lambdaUrl: entity.lambdaUrl,
                }
            );
        }
    );
}

export const useGetL1Measurement = (deviceMeasurementId: string, type: L1MeasurementType = L1MeasurementType.NotSpecified) => {
    return useQuery<L1Measurement>(['l1Measurement', deviceMeasurementId], async () => {
        let url = `/api/device-measurements/${deviceMeasurementId}/l1-measurement`
        addOptionalQueryParam(url, 'type', type);
        const response = await sendGet(url);
        return new L1Measurement(response);
    });
}

export const useGetPatientNote = (
    patientId: string,
    limit?: number,
    type?: string,
    options?: { enabled: boolean }
) => {
    return useQuery<PatientNote[]>(
        ['patientNotes', patientId, limit, type],
        async () => {
            let url = `/api/patients/${patientId}/notes`;
            if (limit !== undefined || type !== undefined) {
                const params = new URLSearchParams();
                if (limit !== undefined) {
                    params.append('limit', limit.toString());
                }
                if (type !== undefined) {
                    params.append('type', type);
                }
                url += `?${params.toString()}`;
            }
            const reply: PatientNote[] = await sendGet(url);
            return reply.map(note => new PatientNote(note));
        },
        options
    );
};

export const useAddPatientNote = () => {
    const queryClient = useQueryClient();
    return useMutation<PatientNote, Error, { note: PatientNote | Partial<PatientNote>, type: PatientNoteType }>(
        async ({ note, type }) => {
            note = new PatientNote(note);

            const payload: Record<string, any> = {
                patientId: note.patient?.id,
                content: note.content,
                time: note.time?.getTime(),
                type: type,
                pictures: note.picturesToUpload,
                attachments: note.attachmentsToUpload,
            };

            if (note.originalId) {
                payload.originalId = note.originalId;
            }
            if (note.ownedByLabelId) {
                payload.ownedByLabelId = note.ownedByLabelId;
            }

            const response: PatientNote = await sendPostFormForJson(
                `/api/patients/${note.patient?.id}/notes`,
                payload
            );
            queryClient.invalidateQueries(['patientNotes', note.patient?.id]);
            queryClient.invalidateQueries(['objectives', note.patient?.id]);
            return new PatientNote(response);
        },
    );
}

export const useUpdatePatientNote = () => {
    return useMutation<void, Error, PatientNote | Partial<PatientNote>>(
        async (entity: Partial<PatientNote>) => {
            entity = new PatientNote(entity);
            await sendPostForm(
                `/api/patients/${entity.patient?.id}/notes/${entity.id}/update-with-pictures`,
                {
                    content: entity.content,
                    pictures: entity.pictures,
                    picturesToUpload: entity.picturesToUpload,
                }
            );
        }
    );
}

export const useTriggerL1Function = () => {
    return useMutation<void, Error, Label>(
        async (entity: Label) => {
            await sendPost(
                `/api/labels/${entity?.id}/l1-measurement/trigger`,
            );
        }
    );
}

export const useTriggerPatientLambda = () => {
    return useMutation<void, Error, { patientId: string; prompt?: string; transcript?: string }>(
        async ({ patientId, prompt, transcript }) => {
            let body: any = {};
            if (prompt) body.prompt = prompt;
            if (transcript) body.transcript = transcript;
            await sendPost(`/api/patients/${patientId}/lambda`, body);
        }
    );
};

export const useTriggerLabelLambda = () => {
    return useMutation<void, Error, { labelId: string; medicalTemplateId?: string; medicalTemplateIds?: string[]; lambdaType?: string }>(
        async ({ labelId, medicalTemplateId, medicalTemplateIds, lambdaType }) => {
            let url = `/api/labels/${labelId}/lambda`;
            if (medicalTemplateId) {
                url = addOptionalQueryParam(url, 'medicalTemplateId', medicalTemplateId);
            }
            if (medicalTemplateIds && medicalTemplateIds.length > 0) {
                // Add each template ID as a separate query parameter
                medicalTemplateIds.forEach(templateId => {
                    url = addOptionalQueryParam(url, 'medicalTemplateIds', templateId);
                });
            }
            if (lambdaType) {
                url = addOptionalQueryParam(url, 'lambdaType', lambdaType);
            }
            // Assuming sendPost returns void or Promise<void>
            await sendPost(url);
        }
    );
};

export const useChangePassword = () => {
    return useMutation<void, { code: string, description: string }[], { oldPassword: string, newPassword: string }>(
        async ({ oldPassword, newPassword }) => {
            await sendPost(
                `/api/users/change-password`,
                {
                    oldPassword,
                    newPassword,
                }
            ).catch(e => {
                throw JSON.parse(e);
            });
        }
    );
}

export const useResetPasswordByEmail = () => {
    const currentUser = useCurrentUser();
    return useMutation<void, string, void>(
        async () => {
            await sendPost(
                `/api/users/forget-password`,
                {
                    email: currentUser.email,
                }
            );
        }
    );
};

export const useChangePreferences = () => {
    return useMutation<void, string, { soapNoteRecipientEmail: string }>(
        async ({ soapNoteRecipientEmail }) => {
            await sendPatch(
                `/api/users`,
                {
                    soapNoteRecipientEmail,
                }
            );
        }
    );
}

// User preferences types and hooks
export interface UserPreferenceDTO {
    defaultLanguage?: string;
    defaultLanguageRegion?: string;
    documentLanguage?: string;
    isFrequentlyUsingMultilingual?: boolean;
    isUsingMetricUnitOnWeight?: boolean;
    isUsingMetricUnitOnTemperature?: boolean;
    pimsInUse?: string;
    roleInVeterinaryField?: string;
    speciesToWorkWith?: string;
    specialInterest?: string;
    isShowingFirstTimeTutorial?: boolean;
}

export const useGetDefaultUserPreferences = () => {
    return useQuery<UserPreferenceDTO>('user-preferences-default', async () => {
        const reply = await sendGet<UserPreferenceDTO>('/api/users/preferences');
        return reply;
    });
}

export const useUpdateUserPreferences = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, Partial<UserPreferenceDTO>>(
        async (preferences) => {
            await sendPatch(
                `/api/users/preferences`,
                {
                    defaultLanguage: preferences.defaultLanguage,
                    defaultLanguageRegion: preferences.defaultLanguageRegion,
                    documentLanguage: preferences.documentLanguage,
                    isFrequentlyUsingMultilingual: preferences.isFrequentlyUsingMultilingual,
                    isUsingMetricUnitOnWeight: preferences.isUsingMetricUnitOnWeight,
                    isUsingMetricUnitOnTemperature: preferences.isUsingMetricUnitOnTemperature,
                    pimsInUse: preferences.pimsInUse,
                    roleInVeterinaryField: preferences.roleInVeterinaryField,
                    speciesToWorkWith: preferences.speciesToWorkWith,
                    specialInterest: preferences.specialInterest,
                    isShowingFirstTimeTutorial: preferences.isShowingFirstTimeTutorial,
                }
            );
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('user-preferences-default');
            }
        }
    );
}

export const useUploadStream = () => {
    return useMutation<void, Error, { deviceSerial: string, fileContents: ArrayBuffer }>(
        async ({ deviceSerial, fileContents }) => {
            const fileBlob = new Blob([fileContents], { type: 'application/octet-stream' });
            const formData = new FormData();
            formData.append('file', fileBlob, 'filename');
            formData.append('deviceSerial', deviceSerial);
            formData.append('deviceSecret', '1e9993c7-66a1-4411-924d-3e44bb239020');

            const response = await fetch('/api/in-stream', {
                method: 'POST',
                body: formData,
                credentials: 'include' // Include cookies or auth headers if needed
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to upload stream: ${errorText}`);
            }
        }
    );
};

export const useGetTwilioToken = (patientId: string) => {
    return useQuery<{ identity: string, token: string, callerId: string }>(['twilioToken', patientId], async () => {
        const response = await sendGet(`/api/voip/token/${patientId}`);
        return response;
    });
};

export const useGetTwilioAccountDetails = (organizationId: string) => {
    return useQuery<TwilioAccountDetails>(['twilioAccountDetails', organizationId], async () => {
        const response = await sendGet(`/api/voip/account-details/${organizationId}`);
        return response;
    }, {
        retry: false, // Disable retries
    });
};

export const useUpdateTwilioAccountDetails = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, TwilioAccountDetails>(
        async (details) => {
            await sendPost(`/api/voip/account-details`, details);
            queryClient.invalidateQueries(['twilioAccountDetails', details.organizationId]);
        }
    );
};

export interface TwilioAccountDetails {
    organizationId: string;
    accountSid: string;
    twimlAppSid: string;
    callerId: string;
    apiSid: string;
    apiSecret: string;
}

export const useGetMedicalTemplates = (organizationId: string) => {
    return useQuery<MedicalTemplate[]>(['medicalTemplates', organizationId], async () => {
        try {
            const reply = await sendGet(`/api/organizations/${organizationId}/medical-template/current`);

            // Check if reply contains the expected structure
            if (reply && reply.content) {
                // Parse the content field
                const parsedContent = JSON.parse(reply.content);
                return [new MedicalTemplate({
                    ...reply,
                    content: parsedContent  // Store the parsed content
                })];
            } else {
                console.error("Unexpected API response format:", reply);
                return [];
            }
        } catch (error) {
            console.error("Error fetching medical templates:", error);
            throw new Error("Failed to fetch medical templates");
        }
    });
};

export const useGetAllMedicalTemplates = (organizationId: string | undefined) => {
    return useQuery<MedicalTemplate[]>(['allMedicalTemplates', organizationId], async () => {
        if (!organizationId) return [];
        try {
            const reply = await sendGet(`/api/organizations/${organizationId}/medical-templates`);

            // Check if reply contains the expected structure
            if (Array.isArray(reply)) {
                return reply.map(template => {
                    // Parse the content field
                    const parsedContent = template.content ? JSON.parse(template.content) : {};
                    return new MedicalTemplate({
                        ...template,
                        content: parsedContent  // Store the parsed content
                    });
                });
            } else {
                console.error("Unexpected API response format:", reply);
                return [];
            }
        } catch (error) {
            console.error("Error fetching all medical templates:", error);
            throw new Error("Failed to fetch all medical templates");
        }
    });
};

export const useGetPatientMedicalTemplates = (orgId: string | undefined, patientId: string | undefined) => {
    return useQuery<MedicalTemplate[]>(['patientMedicalTemplates', orgId, patientId], async () => {
        if (!orgId || !patientId) return [];
        try {
            const reply = await sendGet(`/api/organizations/${orgId}/medical-templates/patient/${patientId}`);

            // Check if reply contains the expected structure
            if (Array.isArray(reply)) {
                return reply.map(template => {
                    // Parse the content field
                    const parsedContent = template.content ? JSON.parse(template.content) : {};
                    return new MedicalTemplate({
                        ...template,
                        content: parsedContent  // Store the parsed content
                    });
                });
            } else {
                console.error("Unexpected API response format:", reply);
                return [];
            }
        } catch (error) {
            console.error("Error fetching patient medical templates:", error);
            throw new Error("Failed to fetch patient medical templates");
        }
    });
};

export const useGetMedicalTemplate = (templateId: string) => {
    return useQuery<MedicalTemplate>(['medicalTemplate', templateId], async () => {
        const reply: MedicalTemplate = await sendGet(`/api/medical-templates/${templateId}`);
        return new MedicalTemplate(reply);
    });
};

export const useDeleteMedicalTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, string>(
        async (templateId: string) => {
            await sendDelete(`/api/medical-templates/${templateId}`);
            queryClient.invalidateQueries(['medicalTemplates']);
        },
    );
}

export const useAddMedicalTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation<MedicalTemplate, Error, Partial<MedicalTemplate>>(
        async (template) => {
            const response: MedicalTemplate = await sendPostForJson(
                `/api/organizations/${template.organizationId}/medical-template`,
                new MedicalTemplate(template).toDTO()  // Use toDTO to convert the content
            );
            queryClient.invalidateQueries(['medicalTemplates', template.organizationId]);
            return new MedicalTemplate(response);
        },
    );
};

export const useUpdateMedicalTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation<MedicalTemplate, Error, MedicalTemplate>(
        async (template) => {
            const response: MedicalTemplate = await sendPatchForJson(
                `/api/organizations/${template.organizationId}/medical-template/${template.id}`,
                new MedicalTemplate(template).toDTO()  // Use toDTO here as well
            );
            queryClient.invalidateQueries(['medicalTemplates', template.organizationId]);
            return new MedicalTemplate(response);
        },
    );
};

export const useSetCurrentMedicalTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { orgId: string, templateId: string }>(
        async ({ orgId, templateId }) => {
            await sendPost(`/api/organizations/${orgId}/medical-template/${templateId}/set-current`);
        },
        {
            onSuccess: (_, { orgId }) => {
                // Invalidate relevant queries after successful update
                queryClient.invalidateQueries(['medicalTemplates', orgId]);
                queryClient.invalidateQueries(['allMedicalTemplates', orgId]);
            }
        }
    );
};

export const useAddL1Measurement = () => {
    return useMutation<void, Error, { labelId: string, l1MeasurementRequest: L1MeasurementRequest }>(
        async ({ labelId, l1MeasurementRequest }) => {
            await sendPost(
                `/api/labels/${labelId}/l1-measurement`,
                l1MeasurementRequest
            );
        }
    );
}

export const usePostInStream = () => {
    return useMutation<void, Error, { file: File, deviceSerial: string }>(
        async ({ file, deviceSerial }) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('deviceSerial', deviceSerial);
            formData.append('deviceSecret', '1e9993c7-66a1-4411-924d-3e44bb239020');

            const response = await fetch('/api/in-stream', {
                method: 'POST',
                body: formData,
                credentials: 'include' // Include cookies or auth headers if needed
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to post in-stream: ${errorText}`);
            }
        }
    );
};

export const useEmailLabelReport = () => {
    return useMutation<void, Error, { labelId: string, emails: string }>(
        async ({ labelId, emails }) => {
            let url = `/api/labels/${labelId}/email-report`;
            url = addOptionalQueryParam(url, 'emails', emails);
            await sendPost(url);
        }
    );
};

export const useGetDefaultPatient = () => {
    return useQuery<Patient>('default-patient', async () => {
        const reply: Patient = await sendGet('/api/patients/default');
        return new Patient(reply);
    });
}

export const useAssignDefaultPatientToLabel = () => {
    return useMutation<void, Error, Label>(
        async (entity: Label) => {
            await sendPost(
                `/api/labels/${entity.id}/assign-from-default`,
                {
                    patientId: entity.patient?.id ?? entity.patientId,
                }
            );
        }
    );
}

export const useSendResetPasswordEmail = () => {
    return useMutation<void, Error, string>(
        async (userId) => {
            let url = `/api/manual-email/SendResetPasswordEmail`;
            url = addOptionalQueryParam(url, 'userId', userId);
            await sendGetWithoutJson(url);
        }
    );
}

export const useSendRegistrationConfirmationEmail = () => {
    return useMutation<void, Error, string>(
        async (userId) => {
            let url = `/api/manual-email/SendRegistrationConfirmationEmail`;
            url = addOptionalQueryParam(url, 'userId', userId);
            await sendGetWithoutJson(url);
        }
    );
}

export const useAddPatient = () => {
    const queryClient = useQueryClient();
    return useMutation<Patient, Error, { patient: Partial<Patient>, file: File } | Partial<Patient>>(
        async (param) => {
            const patient = 'patient' in param ? param.patient : param;
            const response: Patient = await sendPostForJson('/api/patients', patient);

            if ('file' in param) {
                await sendPutForm(`/api/patients/${response.id}/profile-picture`, { file: param.file });
            }

            queryClient.invalidateQueries(['patients']);
            queryClient.invalidateQueries(['search-patients']);
            return new Patient(response);
        },
    );
};

export type GetOrganizationLabelsParams = {
    types?: string[];
    offset?: number;
    limit?: number;
    orderBy?: string;
    sortingProperty?: string;
    isSortingDescending?: boolean;
    createdBy?: string;
    patientSearch?: string;
    deviceSerial?: string;
    status?: string;
    fromTime?: Date;
    toTime?: Date;
}

export const useGetOrganizationLabels = createUseQuery(
    'organization-labels',
    async (organizationId: string | undefined, params?: GetOrganizationLabelsParams) => {
        if (!organizationId) return { data: [], total: 0 };
        
        let url = `/api/organizations/${organizationId}/labels`;
        
        url = appendQueryParams(url, {
            ...params,
            offset: params?.offset ?? 0,
            limit: params?.limit ?? 50,
        });

        const [reply, total] = await sendGetWithTotal<Label[]>(url);
        return {
            data: reply.map(d => new Label(d)),
            total,
        };
    }
);

export const useGetL1Measurements = (labelId: string) => {
    return useQuery<L1Measurement[]>(
        ['l1Measurements', labelId], 
        async () => {
            try {
                const response = await fetch(`/api/labels/${labelId}/l1-measurement`);
                
                // Handle empty responses
                const text = await response.text();
                if (!text || text.trim() === '') {
                    console.log(`Empty response for label ${labelId}, returning empty array`);
                    return [];
                }
                
                // Parse the JSON now that we know it's not empty
                const reply = JSON.parse(text);
                
                return Array.isArray(reply) ? reply.map(measurement => {
                    const vitals = measurement.data || {};
                    return new L1Measurement({
                        ...measurement,
                        temperature: vitals.temperature ?? measurement.temperature,
                        heartRate: vitals.heartRate ?? measurement.heartRate,
                        respiratoryRate: vitals.respiratoryRate ?? measurement.respiratoryRate,
                        spO2: vitals.SpO2 ?? measurement.spO2,
                    });
                }) : [new L1Measurement(reply)];
            } catch (error) {
                console.error('Failed to fetch L1 measurements:', error);
                return [];
            }
        }, 
        {
            // Cache the data for 5 minutes (300000 milliseconds)
            cacheTime: 5 * 60 * 1000,
            
            // Keep the data in cache even when the component unmounts
            staleTime: 5 * 60 * 1000,
            
            // Disable automatic background refetching
            refetchOnWindowFocus: false,
            
            // Add error handling
            onError: (error) => {
                console.error('Failed to fetch L1 measurements:', error);
            },
            
            // Enable the query only if labelId exists
            enabled: !!labelId,
        }
    );
}

export type GetDeviceLabelsParams = {
    labelSetType?: number;
    userId?: string;
    labelType?: string;
    offset?: number;
    limit?: number;
}

export const useGetDeviceLabels = (deviceSerial: string, params?: GetDeviceLabelsParams) => {
    return useQuery<{data: Label[], total: number}>(
        ['deviceLabels', deviceSerial, params], 
        async () => {
            if (!deviceSerial) {
                return { data: [], total: 0 };
            }
            
            const url = appendQueryParams(`/api/devices/${deviceSerial}/labels`, {
                ...params,
                offset: params?.offset ?? 0,
                limit: params?.limit ?? 50,
            });
            
            const [reply, total] = await sendGetWithTotal<Label[]>(url);
            console.log('Device labels API response:', { reply, total, url });
            return {
                data: reply.map(d => new Label(d)),
                total,
            };
        },
        {
            enabled: !!deviceSerial,
            refetchOnWindowFocus: false,
        }
    );
}

export const useDeleteOrganizationMedicalTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { orgId: string, templateId: string }>(
        async ({ orgId, templateId }) => {
            await sendDelete(`/api/organizations/${orgId}/medical-template/${templateId}`);
        },
        {
            onSuccess: (_, { orgId }) => {
                // Invalidate relevant queries after successful deletion
                queryClient.invalidateQueries(['medicalTemplates', orgId]);
                queryClient.invalidateQueries(['allMedicalTemplates', orgId]);
            }
        }
    );
}

export const useUploadPdfTemplate = () => {
    return useMutation<TemplateType, Error, { file: File, organizationId: string }>(
        async ({ file, organizationId }) => {
            const formData = new FormData();
            formData.append('file', file, file.name);
            formData.append('organizationId', organizationId);
            
            const response: TemplateType = await sendPostFormWithFilesForJson('/api/medical-templates/pdf-upload', {
                file: file,
                organizationId: organizationId
            });
            return response;
        }
    );
}

// NOTE: this gets the latest available firmware version from the server
export const useGetNxscopeFirmwareVersion = createUseQuery(
    'nxscope-firmware-version',
    async () => {
      // sendGet will throw on non-2xx and parse JSON for you
      const version = await sendGet<FirmwareVersion>(
        '/api/nxscope-firmware/version'
      )
      return version
    }
)

export const useGetNxhubFirmwareVersion = createUseQuery(
    'nxhub-firmware-version',
    async () => {
      const version = await sendGet<FirmwareVersion>(
        '/api/nxhub-firmware/version'
      )
      return version
    }
)

export interface AvailableFirmwareVersionsResponse {
    versions: string[];
    latestVersion: string;
    totalCount: number;
    // Optional per-version hardware-family details; present once the backend
    // supports hardware families. Versions without details default to "v4".
    versionDetails?: { version: string; hwFamily: string }[];
}

export const useGetNxhubAvailableVersions = (offset: number = 0, limit: number = 10, hwFamily?: string) => {
    return useQuery<AvailableFirmwareVersionsResponse>(
        ['nxhub-available-versions', offset, limit, hwFamily],
        async () => sendGet<AvailableFirmwareVersionsResponse>(
            appendQueryParams('/api/nxhub-firmware/versions', { offset, limit, hwFamily })
        ),
        { refetchOnWindowFocus: false, retry: false, staleTime: 5 * 60 * 1000, keepPreviousData: true }
    );
};

export const checkNxhubFirmwareCommands = async (
    request: NxhubFirmwareCommandsRequest
): Promise<NxhubFirmwareCommandsResponse> => {
    return await sendPostForJson('/api/nxhub-firmware/commands', request);
};

// Note: endpoint gets the firmware zip file and returns the mainboard.signed.bin file as an ArrayBuffer
export const useGetNxscopeFirmwareDownload = createUseQuery(
    'nxscope-firmware-download',
    async () => {
      // 1) fetch the zip as a Blob
      const zipBlob = await sendGetBlob('/api/nxscope-firmware/download')
  
      // 2) unzip it
      const zip = await JSZip.loadAsync(zipBlob)
      const mainboardFile = zip.file('mainboard.signed.bin')
      if (!mainboardFile) {
        throw new Error('mainboard.signed.bin not found in firmware package')
      }
  
      // 3) extract and return the ArrayBuffer
      return await mainboardFile.async('arraybuffer')
    }
)


export type LabelAudioArtifact = 'original' | 'stethoscope-playback' | 'pcg-analysis';

// Direct API call function for use in download handlers
export const downloadLabelAudio = async (labelId: string): Promise<Blob> => {
    const blob = await sendGetBlob(`/api/labels/${labelId}/audio`);
    return blob;
};

// Get a pre-signed URL for downloading a label audio artifact directly from storage
export const getLabelAudioArtifactUrl = async (
    labelId: string,
    artifact: LabelAudioArtifact,
): Promise<{ url: string; expiresInSeconds: number }> => {
    const url = `/api/labels/${labelId}/audio-artifacts/${artifact}/url`;
    const response = await sendGet<{ url: string; expiresInSeconds: number }>(url);
    return response;
};

// Upload a file to S3
export const uploadS3File = async (
    key: string,
    file: File,
    bucketName?: string
): Promise<{ message: string; key: string; bucket: string; s3Url: string; size: number; contentType: string }> => {
    const response = await sendPostFormWithFilesForJson<{ message: string; key: string; bucket: string; s3Url: string; size: number; contentType: string }>('/api/s3-file/upload', {
        Key: key,
        File: file,
        ...(bucketName && { BucketName: bucketName }),
    });
    return response;
};

export const useUploadNxscopeFirmware = () => {
    return useMutation<void, Error, { file: File, version: string, description: string }>(
        async ({ file, version, description }) => {
            await sendPostFormWithFilesForJson('/api/nxscope-firmware/update', {
                FirmwareFile: file,
                Version: version,
                Description: description
            });
        }
    );
}

export const useUploadNxhubFirmware = () => {
    return useMutation<void, Error, { file: File, version: string, description: string, hwFamily?: string }>(
        async ({ file, version, description, hwFamily }) => {
            await sendPostFormWithFilesForJson('/api/nxhub-firmware/update', {
                FirmwareFile: file,
                Version: version,
                Description: description,
                // Optional: omitted for legacy callers. The backend also reads
                // hw_family from manifest.json inside the zip and rejects (400)
                // a mismatch between the two.
                ...(hwFamily && { HwFamily: hwFamily })
            });
        }
    );
}

// NxHub device firmware status types
export type NxHubOperatingMode = 'streaming' | 'batch';

export interface NxHubStatusResponse {
    targetVersion: string;
    currentFirmwareVersion: string | null;
    reportedOrgId: string | null;
    uptimeMs: number | null;
    lastBootCode: string | null;
    lastReportTime: string | null;
    forceVersion: boolean;
    versionTweak: number | null;
    wifiStats: string | null;
    surveyStats: string | null;
    externalFlashStatus: number | null;
    diagnosticStats: string | null;
    // Operating mode: live runtime mode, persisted NVS preference, and admin-set target.
    // null means unreported/not-yet-set. See backend Device.cs for full semantics.
    runtimeMode: NxHubOperatingMode | null;
    persistedMode: NxHubOperatingMode | null;
    targetMode: NxHubOperatingMode | null;
}

// Get NxHub firmware status for a specific device
export const useGetNxHubStatus = (deviceSerial: string) => {
    return useQuery<NxHubStatusResponse>(
        ['nxhub-status', deviceSerial],
        async () => {
            const response = await sendGet<NxHubStatusResponse>(
                `/api/devices/${deviceSerial}/nxhub-status`
            );
            return response;
        },
        {
            enabled: !!deviceSerial,
            refetchOnWindowFocus: false,
            retry: false,
        }
    );
};

// WiFi history types and hook
export interface WifiHistoryPoint {
    timestamp: number;
    current: number | null;
    min: number | null;
    max: number | null;
    avg: number | null;
    channel: number | null;
    bssid: string | null;
    firmwareVersion: string | null;
}

export interface WifiHistoryResponse {
    deviceId: string;
    points: WifiHistoryPoint[];
}

export const useGetWifiHistory = (deviceSerial: string, startMs?: number, endMs?: number) => {
    const now = Date.now();
    const effectiveStart = startMs ?? (now - 24 * 60 * 60 * 1000);
    const effectiveEnd = endMs ?? now;

    return useQuery<WifiHistoryResponse>(
        ['wifi-history', deviceSerial, effectiveStart, effectiveEnd],
        async () => {
            const response = await sendGet<WifiHistoryResponse>(
                `/api/devices/${deviceSerial}/nxhub-wifi-history?startMs=${effectiveStart}&endMs=${effectiveEnd}`
            );
            return response;
        },
        {
            enabled: !!deviceSerial,
            refetchOnWindowFocus: false,
            retry: false,
            staleTime: 5 * 60 * 1000,
        }
    );
};

// Audio activity types and hook
export interface AudioActivityPoint {
    timestamp: number;
    chunksReceived: number;
    totalBytes: number;
    vadSpeechDurationMs: number;
    vadSpeechRatio: number;
    vadIsSilent: boolean;
    isPartial: boolean;
    minutesInBucket: number;
}

export interface AudioActivityStats {
    totalMinutes: number;
    activeMinutes: number;
    silentMinutes: number;
    chunkDeliveryRate: number;
    longestGapMinutes: number;
    speechDetectionRate: number;
    lowSignalAudioImpact: number | null;
}

export interface AudioActivityResponse {
    deviceId: string;
    points: AudioActivityPoint[];
    stats: AudioActivityStats;
    bucketSizeMinutes: number;
}

export const useGetAudioActivity = (deviceSerial: string, startMs?: number, endMs?: number) => {
    const now = Date.now();
    const effectiveStart = startMs ?? (now - 24 * 60 * 60 * 1000);
    const effectiveEnd = endMs ?? now;

    return useQuery<AudioActivityResponse>(
        ['audio-activity', deviceSerial, effectiveStart, effectiveEnd],
        async () => {
            const response = await sendGet<AudioActivityResponse>(
                `/api/devices/${deviceSerial}/nxhub-audio-activity?startMs=${effectiveStart}&endMs=${effectiveEnd}&includeWifiCorrelation=true`
            );
            return response;
        },
        {
            enabled: !!deviceSerial,
            refetchOnWindowFocus: false,
            retry: false,
            staleTime: 5 * 60 * 1000,
        }
    );
};

// Diagnostics history types and hook
export interface DiagnosticsHistoryPoint {
    timestamp: number;
    firmwareVersion: string | null;
    uptimeMs: number | null;
    disconnects: number | null;
    esp32Resets: number | null;
    recoveryCount: number | null;
    httpAge: number | null;
    uploadLatency: number | null;
    maxUploadLatency: number | null;
    liveUploadSuccesses: number | null;
    liveUploadFailures: number | null;
    allocFailures: number | null;
    flashBackupSuccesses: number | null;
    flashBackupFailures: number | null;
    flashDrainQueueDepth: number | null;
    flashEnqueueFailures: number | null;
    drainState: number | null;
    drainReason: number | null;
    drainProgressAge: number | null;
    drainRecoveryActions: number | null;
    ip: string | null;
    gateway: string | null;
    mac: string | null;
    failureSnapshot: string | null;
    pdmDropsBackpressure: number | null;
    pdmDropsOverflow: number | null;
    liveChunksEnqueued: number | null;
    heapFreeBytes: number | null;
    opusEncodeTotalMs: number | null;
    espHeapFree: number | null;
    espHeapMin: number | null;
    espHeapLargestBlock: number | null;
    espCmdLinkUp: number | null;
    pdmCpuMs: number | null;
    allThreadsCpuMs: number | null;
}

export interface DiagnosticsHistoryResponse {
    deviceId: string;
    points: DiagnosticsHistoryPoint[];
}

export const useGetDiagnosticsHistory = (deviceSerial: string, startMs?: number, endMs?: number, enabled: boolean = true) => {
    const now = Date.now();
    const effectiveStart = startMs ?? (now - 24 * 60 * 60 * 1000);
    const effectiveEnd = endMs ?? now;

    return useQuery<DiagnosticsHistoryResponse>(
        ['diagnostics-history', deviceSerial, effectiveStart, effectiveEnd],
        async () => {
            const response = await sendGet<DiagnosticsHistoryResponse>(
                `/api/devices/${deviceSerial}/nxhub-diagnostics-history?startMs=${effectiveStart}&endMs=${effectiveEnd}`
            );
            return response;
        },
        {
            enabled: !!deviceSerial && enabled,
            refetchOnWindowFocus: false,
            retry: false,
            staleTime: 5 * 60 * 1000,
        }
    );
};

// Timeline types and hook
export interface TimelineMinute {
    minuteStartMs: number;
    vadIsSilent: boolean;
    vadSpeechDurationMs: number;
    chunksReceived: number;
    transcriptText: string;
    diarizedTranscriptText?: string;
    conversationId: string;
    conversationStatus: string;
}

export interface TimelineConversation {
    conversationId: string;
    startMinuteMs: number;
    endMinuteMs: number;
    status: string;
    hasTranscript: boolean;
}

export interface DeviceTimelineResponse {
    deviceId: string;
    startMs: number;
    endMs: number;
    totalMinutes: number;
    speechMinutes: number;
    silentMinutes: number;
    minutes: TimelineMinute[];
    conversations: TimelineConversation[];
}

export const useGetDeviceTimeline = (deviceSerial: string, startMs?: number, endMs?: number) => {
    return useQuery<DeviceTimelineResponse>(
        ['device-timeline', deviceSerial, startMs, endMs],
        async () => {
            const response = await sendGet<DeviceTimelineResponse>(
                `/api/nxhub/conversations/timeline/${deviceSerial}?startMs=${startMs}&endMs=${endMs}`
            );
            return response;
        },
        {
            enabled: !!deviceSerial && startMs != null && endMs != null,
            refetchOnWindowFocus: false,
            retry: false,
            staleTime: 5 * 60 * 1000,
        }
    );
};

// Update NxHub target firmware version (admin only)
export const useUpdateNxHubTargetVersion = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, { deviceSerial: string; targetVersion: string; forceVersion: boolean }>(
        async ({ deviceSerial, targetVersion, forceVersion }) => {
            await sendPatch(
                `/api/devices/${deviceSerial}/nxhub-target-version`,
                { targetVersion, forceVersion }
            );
        },
        {
            onSuccess: (_, { deviceSerial }) => {
                queryClient.invalidateQueries(['nxhub-status', deviceSerial]);
                queryClient.invalidateQueries(['devices']);
            }
        }
    );
};

// Update NxHub operating mode target (admin only). Applied on device's next check-in.
export const useUpdateNxHubTargetMode = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, { deviceSerial: string; targetMode: NxHubOperatingMode }>(
        async ({ deviceSerial, targetMode }) => {
            await sendPatch(
                `/api/devices/${deviceSerial}/nxhub-target-mode`,
                { targetMode }
            );
        },
        {
            onSuccess: (_, { deviceSerial }) => {
                queryClient.invalidateQueries(['nxhub-status', deviceSerial]);
            }
        }
    );
};

// Admin-only hwrev (hardware-revision label) pool + update.
export const useGetDeviceHwrevNames = (options?: { enabled?: boolean }) => {
    return useQuery<string[]>(
        ['device-hwrev-names'],
        async () => (await sendGet('/api/devices/hwrev-names')) as string[],
        { enabled: options?.enabled ?? true }
    );
};

export const useUpdateDeviceHwrev = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, { deviceId: string; deviceSerial: string; hwrev: string }>(
        async ({ deviceSerial, hwrev }) => {
            await sendPatch(`/api/devices/${deviceSerial}/hwrev`, { hwrev });
        },
        {
            onSuccess: (_, { deviceId }) => {
                queryClient.invalidateQueries(['devices', deviceId]);
                queryClient.invalidateQueries(['device-hwrev-names']);
            }
        }
    );
};

export const useGetGrowthStatistics = createUseQuery('growth-statistics', async (params: { startDate: string; endDate: string, period: number }) => {
    const reply = await sendPostForJson('/api/statistics/growth', params);
    return reply as {
        users: Record<string, number>;
        organizations: Record<string, number>;
        activeUsers: Record<string, number>;
        labelsCreated: Record<string, number>;
        activeOrganizations: Organization[];
        noLongerActiveOrganizations: Organization[];
    };
});

export type TopUser = {
    userId: string;
    fullName: string;
    email: string;
    organizationName: string;
    totalLabels: number;
    labelBreakdown: Record<string, number>;
};

export const useGetTopUsers = createUseQuery('top-users', async (days = 7, count = 10, labelType?: string) => {
    const params: Record<string, any> = { days, count };
    if (labelType) params.labelType = labelType;
    const url = appendQueryParams('/api/statistics/top-users', params);
    const reply = await sendGet<TopUser[]>(url);
    return reply;
});

export type TopOrganization = {
    organizationId: string;
    organizationName: string;
    totalLabels: number;
    labelBreakdown: Record<string, number>;
    userCount: number;
    deviceCount: number;
};

export const useGetTopOrganizations = createUseQuery('top-organizations', async (days = 7, count = 10, labelType?: string) => {
    const params: Record<string, any> = { days, count };
    if (labelType) params.labelType = labelType;
    const url = appendQueryParams('/api/statistics/top-organizations', params);
    const reply = await sendGet<TopOrganization[]>(url);
    return reply;
});

export type DailyLabelPoint = {
    date: string;
    count: number;
    cumulative: number;
};

export type DeviceLabelBreakdown = {
    deviceId: string;
    serial: string;
    friendlyName: string | null;
    totalLabels: number;
    daily: DailyLabelPoint[];
};

export type OrganizationLabelTrend = {
    organizationId: string;
    organizationName: string;
    baselineCumulative: number;
    daily: DailyLabelPoint[];
    deviceBreakdown: DeviceLabelBreakdown[];
};

export const useGetOrganizationLabelTrend = createUseQuery(
    'organization-label-trend',
    async (organizationId: string | undefined, days = 30, labelType?: string) => {
        if (!organizationId) return null;
        const params: Record<string, any> = { organizationId, days };
        if (labelType) params.labelType = labelType;
        const url = appendQueryParams('/api/statistics/organization-label-trend', params);
        const reply = await sendGet<OrganizationLabelTrend>(url);
        return reply;
    }
);

export const useGetLabelsWithCalibration = createUseQuery('labels-calibration', async (offset = 0, limit = 50) => {
    const url = appendQueryParams('/api/labels/calibration', {
        offset,
        limit,
    });
    const [reply, total] = await sendGetWithTotal<Label[]>(url);
    return {
        data: reply.map(d => new Label(d)),
        total,
    };
});

// Triggers backend aggregation for multiple labels into a medical record
export const useAggregateLabels = () => {
    return useMutation<Label, Error, { labelIds: string[]; patientId: string; medicalTemplateId?: string; apiUrlOverride?: string }>(
        async ({ labelIds, patientId, medicalTemplateId, apiUrlOverride }) => {
            const response = await sendPostForJson(
                `/api/labels/aggregate`,
                {
                    LabelIds: labelIds,
                    PatientId: patientId,
                    MedicalTemplateId: medicalTemplateId,
                    ApiUrlOverride: apiUrlOverride,
                }
            );
            return new Label(response);
        }
    );
};

// NxSCOPE Logs API functions
export const useGetNxScopeLogs = createUseQuery(
    'nxscope-logs',
    async (organizationId: string | undefined, deviceName?: string) => {
        if (!organizationId && !deviceName) return { data: [], total: 0 };

        const params: Record<string, any> = {
            action: 'list'
        };

        if (organizationId) {
            params.organizationId = organizationId;
        }

        if (deviceName) {
            params.deviceName = deviceName;
        }

        const url = appendQueryParams('/api/s3-file/nxscope-logs', params);

        const response: NxScopeLogResponse = await sendGet(url);
        const logs = response.files.map(file => new NxScopeLog(file));

        // Sort by lastModified descending (most recent first)
        logs.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime());

        return {
            data: logs,
            total: logs.length
        };
    }
);

export const useDownloadNxScopeStatistics = () => {
    return useMutation<Blob, Error, { organizationId: string; deviceName: string }>(
        async ({ organizationId, deviceName }) => {
            const url = appendQueryParams('/api/s3-file/nxscope-logs', {
                organizationId,
                deviceName,
                action: 'download-statistics'
            });
            
            const blob = await sendGetBlob(url);
            return blob;
        }
    );
};

export const useDownloadNxScopeLogs = () => {
    return useMutation<Blob, Error, { organizationId: string; deviceName: string }>(
        async ({ organizationId, deviceName }) => {
            const url = appendQueryParams('/api/s3-file/nxscope-logs', {
                organizationId,
                deviceName,
                action: 'download-logs'
            });
            
            const blob = await sendGetBlob(url);
            return blob;
        }
    );
};

// Download individual file by key
export const useDownloadNxScopeFile = () => {
    return useMutation<Blob, Error, { key: string }>(
        async ({ key }) => {
            const url = appendQueryParams('/api/s3-file/nxscope-logs/file', { key });

            const blob = await sendGetBlob(url);
            return blob;
        }
    );
};

// External Credentials API types and hooks
export interface ExternalCredential {
    type: string;
    values: Record<string, string>;
    createdBy: string;
    createdAt: Date;
    updatedBy: string;
    updatedAt: Date;
}

export interface AddExternalCredentialDTO {
    type: string;
    values: Record<string, string>;
}

export interface UpdateExternalCredentialDTO {
    type: string;
    values: Record<string, string>;
}

export interface InstinctConnectionTestResponse {
    integration_type: string;
    integration_url: string;
    status: string;
    expires_in: number;
    token_type: string;
}

// Get all external credentials for the organization
export const useGetExternalCredentials = createUseQuery(
    'external-credentials',
    async (organizationId: string | undefined, offset = 0, limit = 50) => {
        if (!organizationId) return { data: [], total: 0 };

        const url = appendQueryParams(`/api/organizations/${organizationId}/external-credentials`, {
            offset,
            limit,
        });
        const [reply, total] = await sendGetWithTotal<ExternalCredential[]>(url);
        return {
            data: reply,
            total,
        };
    }
);

// Get a specific external credential by type
export const useGetExternalCredential = createUseQuery(
    'external-credential',
    async (organizationId: string | undefined, type: string | undefined) => {
        if (!organizationId || !type) return null;
        const reply: ExternalCredential = await sendGet(`/api/organizations/${organizationId}/external-credentials/${type}`);
        return reply;
    }
);

// Create a new external credential
export const useCreateExternalCredential = () => {
    const queryClient = useQueryClient();
    return useMutation<ExternalCredential, Error, AddExternalCredentialDTO & { organizationId: string }>(
        async ({ organizationId, type, values }) => {
            const response: ExternalCredential = await sendPostForJson(
                `/api/organizations/${organizationId}/external-credentials`,
                { type, values }
            );
            return response;
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['external-credentials', variables.organizationId]);
            }
        }
    );
};

// Update an existing external credential
export const useUpdateExternalCredential = () => {
    const queryClient = useQueryClient();
    return useMutation<ExternalCredential, Error, UpdateExternalCredentialDTO & { organizationId: string }>(
        async ({ organizationId, type, values }) => {
            const response: ExternalCredential = await sendPutForJson(
                `/api/organizations/${organizationId}/external-credentials/${type}`,
                { type, values }
            );
            return response;
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['external-credentials', variables.organizationId]);
                queryClient.invalidateQueries(['external-credential', variables.organizationId, variables.type]);
            }
        }
    );
};

// Delete an external credential
export const useDeleteExternalCredential = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { organizationId: string; type: string }>(
        async ({ organizationId, type }) => {
            await sendDelete(`/api/organizations/${organizationId}/external-credentials/${type}`);
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['external-credentials', variables.organizationId]);
                queryClient.invalidateQueries(['external-credential', variables.organizationId, variables.type]);
            }
        }
    );
};

// Instinct Integration API
export interface InstinctConnectionTestResponse {
    integration_type: string;
    integration_url: string;
    status: string;
    expires_in: number;
    token_type: string;
}

// Scoped Token API
export interface ScopedTokenResponse {
    token: string;
    expiresAt: string;
    scopes: string[];
}

export interface GenerateScopedTokenRequest {
    scopes: string[];
}

// Generate a scoped access token with specified scopes
export const useGenerateScopedToken = () => {
    return useMutation<ScopedTokenResponse, Error, GenerateScopedTokenRequest>(
        async ({ scopes }) => {
            const response: ScopedTokenResponse = await sendPostForJson(
                '/api/auth/generate-scoped-token',
                { scopes }
            );
            return response;
        }
    );
};

// NxMic API
export interface NxMicAppVersions {
    desktop: string;
    ios: string;
    android: string;
}

export interface NxMicInfo {
    url: string;
    soapServiceUrl: string;
    apiVersion: string;
    timeoutSeconds: number;
    maxUploadSizeBytes: number;
    supportedFileTypes: string[];
    minimumSupportedVersions: NxMicAppVersions;
}

// Get NxMic URL and configuration info
export const useGetNxMicInfo = createUseQuery(
    'nxmic-info',
    async () => {
        const response: NxMicInfo = await sendGet('/api/nxmic/info');
        return response;
    }
);

// ===== NxMic Admin Access Management API =====
export interface NxMicOrgAccessResponse {
    organizationId: string;
    organizationName: string;
    hasNxMicAccess: boolean;
    message: string;
}

export interface NxHubAccessCacheResponse {
    organizationId: string;
    organizationName: string;
    message: string;
}

export interface NxMicOrgListItem {
    id: string;
    name: string;
    standaloneFeatures: string[];
}

export const useCheckNxMicOrgAccess = (organizationId: string) => {
    return useQuery<NxMicOrgAccessResponse>(
        ['nxmic-org-access', organizationId],
        async () => {
            const url = appendQueryParams('/api/admin/nxmic/check-org-access', { organizationId });
            const response: NxMicOrgAccessResponse = await sendGet(url);
            return response;
        },
        {
            enabled: !!organizationId,
            retry: false,
        }
    );
};

export const useSetNxMicOrgAccess = () => {
    const queryClient = useQueryClient();
    return useMutation<NxMicOrgAccessResponse, Error, { organizationId: string; allowAccess: boolean }>(
        async ({ organizationId, allowAccess }) => {
            const response: NxMicOrgAccessResponse = await sendPostForJson(
                '/api/admin/nxmic/set-org-access',
                { organizationId, allowAccess }
            );
            return response;
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['nxmic-org-access', variables.organizationId]);
                queryClient.invalidateQueries(['nxmic-orgs-list']);
            }
        }
    );
};

export const useClearNxMicOrgAccessCache = () => {
    const queryClient = useQueryClient();
    return useMutation<NxHubAccessCacheResponse, Error, { organizationId: string }>(
        async ({ organizationId }) => {
            const response: NxHubAccessCacheResponse = await sendPostForJson(
                '/api/admin/nxmic/clear-org-access-cache',
                { organizationId }
            );
            return response;
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['nxmic-org-access', variables.organizationId]);
            }
        }
    );
};

export const useListNxMicOrgs = () => {
    return useQuery<NxMicOrgListItem[]>(
        ['nxmic-orgs-list'],
        async () => {
            const response: NxMicOrgListItem[] = await sendGet('/api/admin/nxmic/list-orgs');
            return response;
        },
        { retry: false }
    );
};

// ===== NxHUB Free-Device Coupon Admin API =====
export interface NxHubOrgCouponResponse {
    organizationId: string;
    organizationName: string;
    couponId: string | null;
    message: string;
}

export interface NxHubAllowedCouponsResponse {
    allowedCouponIds: string[];
}

export const useGetOrgNxHubFreeDeviceCoupon = (organizationId: string) => {
    return useQuery<NxHubOrgCouponResponse>(
        ['nxhub-org-coupon', organizationId],
        async () => {
            const url = appendQueryParams('/api/admin/nxhub-coupon/check-org-coupon', { organizationId });
            const response: NxHubOrgCouponResponse = await sendGet(url);
            return response;
        },
        {
            enabled: !!organizationId,
            retry: false,
        }
    );
};

export const useSetOrgNxHubFreeDeviceCoupon = () => {
    const queryClient = useQueryClient();
    return useMutation<NxHubOrgCouponResponse, Error, { organizationId: string; couponId: string | null }>(
        async ({ organizationId, couponId }) => {
            const response: NxHubOrgCouponResponse = await sendPostForJson(
                '/api/admin/nxhub-coupon/set-org-coupon',
                { organizationId, couponId }
            );
            return response;
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['nxhub-org-coupon', variables.organizationId]);
            }
        }
    );
};

export const useGetAllowedNxHubFreeDeviceCoupons = () => {
    return useQuery<NxHubAllowedCouponsResponse>(
        ['nxhub-allowed-coupons'],
        async () => {
            const response: NxHubAllowedCouponsResponse = await sendGet('/api/admin/nxhub-coupon/allowed-coupons');
            return response;
        },
        { retry: false }
    );
};

// ===== SOAP Admin Org Config =====
export interface SoapOrgEnabledResponse {
    organizationId: string;
    organizationName: string;
    isSoapEnabled: boolean;
    message: string;
}

export const useCheckSoapOrgEnabled = (organizationId: string) => {
    return useQuery<SoapOrgEnabledResponse>(
        ['soap-org-enabled', organizationId],
        async () => {
            const url = appendQueryParams('/api/admin/soap/check-org-soap-enabled', { organizationId });
            const response: SoapOrgEnabledResponse = await sendGet(url);
            return response;
        },
        {
            enabled: !!organizationId,
            retry: false,
        }
    );
};

export const useSetSoapOrgEnabled = () => {
    const queryClient = useQueryClient();
    return useMutation<SoapOrgEnabledResponse, Error, { organizationId: string; soapEnabled: boolean }>(
        async ({ organizationId, soapEnabled }) => {
            const response: SoapOrgEnabledResponse = await sendPostForJson(
                '/api/admin/soap/set-org-soap-enabled',
                { organizationId, soapEnabled }
            );
            return response;
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['soap-org-enabled', variables.organizationId]);
            }
        }
    );
};

// ===== Speaker Identification API =====
import { SpeakerEmbedding, SpeakerIdentificationResult, EnrollSpeakerResponse, IdentifySpeakerResponse } from 'web/model/SpeakerIdentification';

// Get all enrolled speakers for an organization
export const useGetOrganizationSpeakers = createUseQuery(
    'organization-speakers',
    async (organizationId: string | undefined) => {
        if (!organizationId) return [];
        const reply: SpeakerEmbedding[] = await sendGet(`/api/organizations/${organizationId}/speakers`);
        return reply.map(s => new SpeakerEmbedding(s));
    }
);

// Get speaker embedding for a specific user
export const useGetSpeakerEmbedding = createUseQuery(
    'speaker-embedding',
    async (organizationId: string | undefined, userId: string | undefined) => {
        if (!organizationId || !userId) return null;
        const reply: SpeakerEmbedding = await sendGet(`/api/organizations/${organizationId}/speakers/users/${userId}`);
        return new SpeakerEmbedding(reply);
    }
);

// Get speaker embedding by ID
export const useGetSpeakerEmbeddingById = createUseQuery(
    'speaker-embedding-by-id',
    async (organizationId: string | undefined, embeddingId: string | undefined) => {
        if (!organizationId || !embeddingId) return null;
        const reply: SpeakerEmbedding = await sendGet(`/api/organizations/${organizationId}/speakers/${embeddingId}`);
        return new SpeakerEmbedding(reply);
    }
);

// Get identification result for a label
export const useGetIdentificationResult = createUseQuery(
    'identification-result',
    async (organizationId: string | undefined, labelId: string | undefined) => {
        if (!organizationId || !labelId) return null;
        const reply: SpeakerIdentificationResult = await sendGet(
            `/api/organizations/${organizationId}/speakers/labels/${labelId}/identification`
        );
        return new SpeakerIdentificationResult(reply);
    }
);

// Get user's identification history
export const useGetUserIdentificationHistory = createUseQuery(
    'user-identification-history',
    async (organizationId: string | undefined, userId: string | undefined) => {
        if (!organizationId || !userId) return [];
        const reply: SpeakerIdentificationResult[] = await sendGet(
            `/api/organizations/${organizationId}/speakers/users/${userId}/history`
        );
        return reply.map(r => new SpeakerIdentificationResult(r));
    }
);

// Enroll a speaker (supports both user-based and non-user enrollment)
export const useEnrollSpeaker = () => {
    const queryClient = useQueryClient();
    return useMutation<EnrollSpeakerResponse, Error, {
        organizationId: string;
        userId?: string;
        speakerIdentifier?: string;
        displayName?: string;
        customMetadata?: Record<string, string>;
        audioFileUrl: string;
    }>(
        async ({ organizationId, userId, speakerIdentifier, displayName, customMetadata, audioFileUrl }) => {
            const response: EnrollSpeakerResponse = await sendPostForJson(
                `/api/organizations/${organizationId}/speakers/enroll`,
                { userId, speakerIdentifier, displayName, customMetadata, audioFileUrl }
            );
            return response;
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['organization-speakers', variables.organizationId]);
                if (variables.userId) {
                    queryClient.invalidateQueries(['speaker-embedding', variables.organizationId, variables.userId]);
                }
            }
        }
    );
};

// Identify a speaker
export const useIdentifySpeaker = () => {
    return useMutation<IdentifySpeakerResponse, Error, { organizationId: string; labelId: string; audioFileUrl: string }>(
        async ({ organizationId, labelId, audioFileUrl }) => {
            const response: IdentifySpeakerResponse = await sendPostForJson(
                `/api/organizations/${organizationId}/speakers/identify`,
                { labelId, audioFileUrl }
            );
            return response;
        }
    );
};

// Delete speaker embedding (by embeddingId)
export const useDeleteSpeakerEmbedding = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { organizationId: string; embeddingId: string }>(
        async ({ organizationId, embeddingId }) => {
            await sendDelete(`/api/organizations/${organizationId}/speakers/${embeddingId}`);
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['organization-speakers', variables.organizationId]);
            }
        }
    );
};

// ===== API Key Hooks =====
import {
    CreateApiKeyRequest, CreateApiKeyResponse, ApiKeyListItem,
    CreateWebhookRequest, CreateWebhookResponse, UpdateWebhookRequest, WebhookListItem,
    WebhookDeliveryLogItem
} from 'web/types/Developer';

// List user's API keys (user-scoped, no params)
export const useGetApiKeys = () => {
    return useQuery<ApiKeyListItem[]>('api-keys', async () => {
        const reply: ApiKeyListItem[] = await sendGet('/api/api-keys');
        return reply;
    });
};

// Create a new API key (returns full key once)
export const useCreateApiKey = () => {
    const queryClient = useQueryClient();
    return useMutation<CreateApiKeyResponse, Error, CreateApiKeyRequest>(
        async (request) => {
            const response: CreateApiKeyResponse = await sendPostForJson(
                '/api/api-keys',
                request
            );
            return response;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('api-keys');
            }
        }
    );
};

// Revoke an API key
export const useRevokeApiKey = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, string>(
        async (id) => {
            await sendDelete(`/api/api-keys/${id}`);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('api-keys');
            }
        }
    );
};

// Admin: List all API keys
export const useGetApiKeysAdmin = () => {
    return useQuery<ApiKeyListItem[]>('api-keys-admin', async () => {
        const reply: ApiKeyListItem[] = await sendGet('/api/api-keys/admin');
        return reply;
    });
};

// Admin: Revoke any API key
export const useRevokeApiKeyAdmin = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, string>(
        async (id) => {
            await sendDelete(`/api/api-keys/admin/${id}`);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('api-keys-admin');
            }
        }
    );
};

// ===== Webhook Hooks =====

// List org webhooks
export const useGetWebhooks = createUseQuery(
    'webhooks',
    async (organizationId: string | undefined) => {
        if (!organizationId) return [];
        const reply: WebhookListItem[] = await sendGet(
            `/api/organizations/${organizationId}/webhooks`
        );
        return reply;
    }
);

// Create a webhook
export const useCreateWebhook = () => {
    const queryClient = useQueryClient();
    return useMutation<CreateWebhookResponse, Error, CreateWebhookRequest & { organizationId: string }>(
        async ({ organizationId, ...request }) => {
            const response: CreateWebhookResponse = await sendPostForJson(
                `/api/organizations/${organizationId}/webhooks`,
                request
            );
            return response;
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['webhooks', variables.organizationId]);
            }
        }
    );
};

// Update a webhook
export const useUpdateWebhook = () => {
    const queryClient = useQueryClient();
    return useMutation<WebhookListItem, Error, UpdateWebhookRequest & { organizationId: string; webhookId: string }>(
        async ({ organizationId, webhookId, ...request }) => {
            const response: WebhookListItem = await sendPatchForJson(
                `/api/organizations/${organizationId}/webhooks/${webhookId}`,
                request
            );
            return response;
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['webhooks', variables.organizationId]);
            }
        }
    );
};

// Delete a webhook
export const useDeleteWebhook = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { organizationId: string; webhookId: string }>(
        async ({ organizationId, webhookId }) => {
            await sendDelete(`/api/organizations/${organizationId}/webhooks/${webhookId}`);
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['webhooks', variables.organizationId]);
            }
        }
    );
};

// Test a webhook (send ping)
export const useTestWebhook = () => {
    return useMutation<void, Error, { organizationId: string; webhookId: string }>(
        async ({ organizationId, webhookId }) => {
            await sendPost(`/api/organizations/${organizationId}/webhooks/${webhookId}/test`);
        }
    );
};

// Re-enable a disabled webhook
export const useEnableWebhook = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { organizationId: string; webhookId: string }>(
        async ({ organizationId, webhookId }) => {
            await sendPost(`/api/organizations/${organizationId}/webhooks/${webhookId}/enable`);
        },
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['webhooks', variables.organizationId]);
            }
        }
    );
};

// Get delivery logs for a webhook
export const useGetWebhookDeliveryLogs = createUseQuery(
    'webhook-delivery-logs',
    async (organizationId: string | undefined, webhookId: string | undefined) => {
        if (!organizationId || !webhookId) return [];
        const reply: WebhookDeliveryLogItem[] = await sendGet(
            `/api/organizations/${organizationId}/webhooks/${webhookId}/deliveries`
        );
        return reply;
    }
);

// Admin: List all webhooks
export const useGetWebhooksAdmin = () => {
    return useQuery<WebhookListItem[]>('webhooks-admin', async () => {
        const reply: WebhookListItem[] = await sendGet('/api/webhooks/admin');
        return reply;
    });
};

// Admin: Delete any webhook
export const useDeleteWebhookAdmin = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, string>(
        async (id) => {
            await sendDelete(`/api/webhooks/admin/${id}`);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('webhooks-admin');
            }
        }
    );
};

// Admin: Get delivery logs for any webhook
export const useGetWebhookDeliveryLogsAdmin = createUseQuery(
    'webhook-delivery-logs-admin',
    async (webhookId: string | undefined) => {
        if (!webhookId) return [];
        const reply: WebhookDeliveryLogItem[] = await sendGet(
            `/api/webhooks/admin/${webhookId}/deliveries`
        );
        return reply;
    }
);

// NxHub Firmware Analytics types
export interface NxHubFirmwareAnalyticsParams {
    organizationId?: string;
    reportedOrganizationId?: string;
    firmwareVersion?: string;
    hwFamily?: string;
    offset: number;
    limit: number;
}

export interface NxHubFirmwareSummary {
    totalDevices: number;
    reportedDevices: number;
    neverReportedDevices: number;
    upToDateDevices: number;
    needsUpdateDevices: number;
    onlineDevices: number;
    offlineDevices: number;
}

export interface NxHubFirmwareVersionCount {
    version: string;
    count: number;
}

export interface NxHubFirmwareDeviceItem {
    id: string;
    serial: string;
    friendlyName: string | null;
    currentFirmwareVersion: string | null;
    targetFirmwareVersion: string | null;
    organizationName: string | null;
    organizationId: string | null;
    reportedOrganizationName: string | null;
    reportedOrganizationId: string | null;
    lastReportTime: string | null;
    lastBootCode: string | null;
    uptimeMs: number | null;
    status: string;
    lastAudioTime: string | null;
    versionTweak: number | null;
    wifiStats: string | null;
    surveyStats: string | null;
    externalFlashStatus: number | null;
    diagnosticStats: string | null;
    // Optional hardware-revision fields; present once the backend reports
    // them. hwrev is the free-text PCB label, hwFamily the derived family.
    hwrev?: string | null;
    hwFamily?: string | null;
}

export interface NxHubFirmwareAnalyticsResponse {
    summary: NxHubFirmwareSummary;
    versionDistribution: NxHubFirmwareVersionCount[];
    devices: NxHubFirmwareDeviceItem[];
    totalFilteredDevices: number;
}

export interface NxHubFleetHistoryResponse {
    reportingDevices: Record<string, number>;
}

export interface NxHubUtilizationSummary {
    totalCustomerDevices: number;
    activeDevices: number;
    totalCustomerOrganizations: number;
    activeOrganizations: number;
    totalSessions: number;
    buttonSessions: number;
    nxMicSessions: number;
    totalRecordingMs: number;
    buttonRecordingMs: number;
    nxMicRecordingMs: number;
    sessionsPerDay: number;
    recordingHoursPerDay: number;
    activeDeviceRate: number;
    stale14Devices: number;
    stale21Devices: number;
    stale30Devices: number;
}

export interface NxHubUtilizationBucket {
    activeDevices: number;
    totalSessions: number;
    buttonSessions: number;
    nxMicSessions: number;
    totalRecordingMs: number;
    buttonRecordingMs: number;
    nxMicRecordingMs: number;
}

export interface NxHubUtilizationOrganizationRow {
    organizationName: string;
    totalDevices: number;
    activeDevices: number;
    utilizationRate: number;
    totalSessions: number;
    buttonSessions: number;
    nxMicSessions: number;
    totalRecordingMs: number;
    sessionsPerDay: number;
    lastActivityAt: string | null;
    daysSinceLastActivity: number | null;
    stale14: boolean;
    stale21: boolean;
    stale30: boolean;
    dominantFirmwareVersion: string;
}

export interface NxHubUtilizationFirmwareRow {
    firmwareVersion: string;
    totalDevices: number;
    activeDevices: number;
    utilizationRate: number;
    totalSessions: number;
    buttonSessions: number;
    nxMicSessions: number;
    totalRecordingMs: number;
    sessionsPerDay: number;
}

export interface NxHubUtilizationDeviceRow {
    serial: string;
    friendlyName: string | null;
    organizationName: string | null;
    currentFirmwareVersion: string | null;
    totalSessions: number;
    buttonSessions: number;
    nxMicSessions: number;
    totalRecordingMs: number;
    sessionsPerDay: number;
    lastActivityAt: string | null;
    daysSinceLastActivity: number | null;
    hasActivityInRange: boolean;
    stale14: boolean;
    stale21: boolean;
    stale30: boolean;
}

export interface NxHubUtilizationResponse {
    summary: NxHubUtilizationSummary;
    buckets: Record<string, NxHubUtilizationBucket>;
    organizations: NxHubUtilizationOrganizationRow[];
    firmware: NxHubUtilizationFirmwareRow[];
    devices: NxHubUtilizationDeviceRow[];
    excludedDeviceCount: number;
    excludedOrganizationCount: number;
    excludedOrganizationNames: string[];
}

export const useGetNxHubFirmwareAnalytics = (params: NxHubFirmwareAnalyticsParams) => {
    return useQuery<NxHubFirmwareAnalyticsResponse>(
        ['nxhub-firmware-analytics', params],
        async () => {
            const url = appendQueryParams('/api/admin/nxhub-firmware-analytics', {
                OrganizationId: params.organizationId,
                ReportedOrganizationId: params.reportedOrganizationId,
                FirmwareVersion: params.firmwareVersion,
                HwFamily: params.hwFamily,
                Offset: params.offset,
                Limit: params.limit,
            });
            return sendGet<NxHubFirmwareAnalyticsResponse>(url);
        }
    );
};

export const useUpdateNxHubDeviceOrg = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, { deviceSerial: string; field: 'organizationId' | 'reportedOrganizationId' | 'both'; organizationId: string | null }>(
        async ({ deviceSerial, field, organizationId }) => {
            await sendPatch(
                '/api/admin/nxhub-firmware-analytics/device-org',
                { deviceSerial, field, organizationId: organizationId || '' }
            );
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['nxhub-firmware-analytics']);
            }
        }
    );
};

export interface NxHubOrgStatusParams {
    firmwareVersion?: string;
    offset: number;
    limit: number;
}

export const useGetNxHubOrgStatus = (params: NxHubOrgStatusParams) => {
    return useQuery<NxHubFirmwareAnalyticsResponse>(
        ['nxhub-org-status', params],
        async () => {
            const url = appendQueryParams('/api/nxhub-status', {
                FirmwareVersion: params.firmwareVersion,
                Offset: params.offset,
                Limit: params.limit,
            });
            return sendGet<NxHubFirmwareAnalyticsResponse>(url);
        },
        {
            refetchInterval: 60000,
        }
    );
};

export const useGetNxHubFleetHistory = createUseQuery(
    'nxhub-fleet-history',
    async (params: { startDate: string; endDate: string; period: number }) => {
        const reply = await sendPostForJson('/api/admin/nxhub-firmware-analytics/history', params);
        return reply as NxHubFleetHistoryResponse;
    }
);

export const useGetNxHubUtilization = createUseQuery(
    'nxhub-utilization',
    async (params: { startDate: string; endDate: string; period: number }) => {
        const reply = await sendPostForJson('/api/admin/nxhub-firmware-analytics/utilization', params);
        return reply as NxHubUtilizationResponse;
    }
);

// ============================================================================
// NxHUB Conversations (Recordings) — used by /nxhub-recordings page
// ============================================================================

export interface NxHubSpeakerMatch {
    conversationSpeakerId: number;
    speakerProfileId?: string;
    matchType?: string;
    matchScore?: number;
    totalSpeechSec?: number;
    assignedId?: string;
    displayName?: string;
}

export interface NxHubConversationItem {
    deviceId: string;
    conversationId: string;
    organizationId: string;
    userId?: string;
    userEmail?: string;
    organizationName?: string;
    status: string;
    startMinuteMs: number;
    endMinuteMs: number;
    lastSpeechMinuteMs?: number;
    speechMinuteCount: number;
    silentMinuteCount: number;
    consecutiveSilentCount?: number;
    totalSpeechDurationMs: number;
    createdAt?: number;
    updatedAt?: number;
    completedAt?: number;
    isIncomplete?: boolean;
    version?: number;
    hasTranscript: boolean;
    transcriptText?: string;
    nxvetLabelId?: string;
    speakerProfilingStatus?: string;
    speakerProfilingUpdatedAt?: number;
    speakerMatches?: NxHubSpeakerMatch[];

    // Vet / staff names extracted by the server from the linked NxVET
    // Label's metadata.petData. Either or both can be empty when the label
    // is missing, the SOAP extraction didn't run, or the PIMS didn't fill
    // these slots. `staffRole` tells the UI which slot filled the primary
    // name ("vet" | "staff") so the chip can render without re-deriving.
    vetName?: string;
    staffName?: string;
    staffRole?: 'vet' | 'staff';

    // Rows synthesized from NxVET labels rather than DynamoDB conversation
    // records. Used by /nxhub-recordings to include legacy/button-mode
    // NxHubBatch labels in the same stats and room/staff lists as NxMIC
    // conversations.
    sourceLabelId?: string;
    sourceLabelType?: string;
    detailUrl?: string;
}

export interface ListNxHubConversationsResponse {
    items: NxHubConversationItem[];
    nextPageToken?: string;
    hasMore?: boolean;
}

export interface GetNxHubConversationsParams {
    organizationId?: string;
    deviceId?: string;
    status?: string;
    startMs?: number;
    endMs?: number;
    pageSize?: number;
    pageToken?: string;
}

// Server caps PageSize at 100 (see ListNxHubConversationsQuery on the backend).
// We auto-page on the client side up to MAX_PAGES so the Soundbook hero can
// show a full month for busy clinics without callers having to manage tokens.
const NXHUB_CONVERSATIONS_MAX_PAGE_SIZE = 100;
const NXHUB_CONVERSATIONS_MAX_PAGES = 10; // hard cap = 1000 items per window
// The server rate-limits per user (~20 req/s). The status page already fans out
// across several queries (org status, per-device audio activity, per-row record
// polls), so a 10-page burst fired with no gap can tip the whole page over the
// limit and 429. A small gap between pages keeps a full-window fetch under it.
const NXHUB_CONVERSATIONS_PAGE_DELAY_MS = 120;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const useGetNxHubConversations = (
    params: GetNxHubConversationsParams,
    enabled: boolean = true,
    // Optional per-call overrides. `maxPages` caps live views that only need
    // the current set, not a full history. `allowPartialPageErrors` is opt-in
    // for those live views so full-window/history callers still surface any
    // later-page failure instead of silently truncating analytics.
    options?: { refetchInterval?: number | false; maxPages?: number; allowPartialPageErrors?: boolean },
) => {
    return useQuery<ListNxHubConversationsResponse>(
        [
            'nxhub-conversations',
            params,
            options?.maxPages ?? NXHUB_CONVERSATIONS_MAX_PAGES,
            options?.allowPartialPageErrors ?? false,
        ],
        async () => {
            const requestedPageSize = Math.min(
                NXHUB_CONVERSATIONS_MAX_PAGE_SIZE,
                Math.max(1, params.pageSize ?? 50),
            );
            const maxPages = Math.max(
                1,
                Math.min(options?.maxPages ?? NXHUB_CONVERSATIONS_MAX_PAGES, NXHUB_CONVERSATIONS_MAX_PAGES),
            );
            const aggregated: NxHubConversationItem[] = [];
            let pageToken: string | undefined = params.pageToken;
            let lastResponse: ListNxHubConversationsResponse | undefined;

            for (let i = 0; i < maxPages; i++) {
                const url = appendQueryParams('/api/nxhub/conversations', {
                    organizationId: params.organizationId,
                    deviceId: params.deviceId,
                    status: params.status,
                    startMs: params.startMs,
                    endMs: params.endMs,
                    pageSize: requestedPageSize,
                    pageToken,
                });
                try {
                    lastResponse = await sendGet<ListNxHubConversationsResponse>(url);
                } catch (err) {
                    // Live views can keep already-fetched pages if a later page
                    // hits the per-user limit; full history/reporting callers
                    // must continue surfacing partial fetch failures.
                    if (i === 0 || !options?.allowPartialPageErrors) throw err;
                    break;
                }
                if (lastResponse?.items?.length) {
                    aggregated.push(...lastResponse.items);
                }
                pageToken = lastResponse?.nextPageToken;
                if (!pageToken || !lastResponse?.hasMore) break;
                // Space out the next page so a full-window fetch doesn't burst.
                await sleep(NXHUB_CONVERSATIONS_PAGE_DELAY_MS);
            }

            return {
                items: aggregated,
                nextPageToken: pageToken,
                hasMore: !!pageToken,
            };
        },
        {
            enabled,
            refetchOnWindowFocus: false,
            staleTime: 60 * 1000,
            refetchInterval: options?.refetchInterval,
        }
    );
};

// ─── Daily activity (server-aggregated) ─────────────────────────────────
// Per-day activity rollup for the NxHUB Intelligence "Daily activity" calendar
// and its headline KPIs. The list query above caps a window at ~1000 rows
// (auto-paged newest-first), so a busy clinic's OLDEST days fall off the cap and
// the heatmap renders them as falsely quiet. This endpoint buckets the whole
// window server-side (no cap) and returns only small per-day totals.

export interface NxHubDailyActivityDay {
    date: string;        // "yyyy-MM-dd" in the offset we sent
    activityMs: number;  // speech-or-wallclock ms attributed to that local day
    conversations: number;
}

export interface NxHubDailyActivityResponse {
    days: NxHubDailyActivityDay[];
    totalActivityMs: number;
    totalConversations: number;
    busiestRoomDeviceId?: string;
    busiestRoomActivityMs: number;
    // True only if the per-org scan hit its server-side safety cap (a window
    // far larger than the 30-day presets). Normally false.
    truncated: boolean;
}

export interface GetNxHubDailyActivityParams {
    organizationId?: string;
    startMs: number;
    endMs: number;
    // Preferred: the browser's IANA timezone (Intl.DateTimeFormat().resolvedOptions().timeZone,
    // e.g. "America/New_York"). The server cuts day boundaries on LOCAL midnight
    // in this zone PER timestamp, so a range viewed in a different DST season —
    // or one straddling a DST change — still buckets each conversation on the
    // same calendar day the heatmap renders it. Falls back to utcOffsetMinutes
    // server-side when absent/unknown.
    timeZoneId?: string;
    // Fallback only: minutes to ADD to UTC to reach local wall clock, i.e.
    // `-new Date().getTimezoneOffset()`. Used when timeZoneId is unavailable;
    // accurate only within a single DST season.
    utcOffsetMinutes: number;
}

export const useGetNxHubDailyActivity = (
    params: GetNxHubDailyActivityParams,
    enabled: boolean = true,
) => {
    return useQuery<NxHubDailyActivityResponse>(
        ['nxhub-daily-activity', params],
        async () => {
            const url = appendQueryParams('/api/nxhub/conversations/daily-activity', {
                organizationId: params.organizationId,
                startMs: params.startMs,
                endMs: params.endMs,
                timeZoneId: params.timeZoneId,
                utcOffsetMinutes: params.utcOffsetMinutes,
            });
            return sendGet<NxHubDailyActivityResponse>(url);
        },
        {
            enabled,
            refetchOnWindowFocus: false,
            staleTime: 60 * 1000,
        }
    );
};

// ─── Proactive insights (Phase 3) ───────────────────────────────────────

export interface NxHubInsight {
    id: string;
    tone: 'positive' | 'negative' | 'neutral' | 'attention';
    icon: string;
    title: string;
    detail: string;
    suggestedPrompt: string;
}

export interface NxHubInsightsResponse {
    insights: NxHubInsight[];
    truncated: boolean;
}

// Mirrors GetNxHubDailyActivityParams — same window + tz convention. Always pass
// organizationId explicitly (admins are not auto-scoped to a primary org).
export const useGetNxHubInsights = (
    params: GetNxHubDailyActivityParams,
    enabled: boolean = true,
) => {
    return useQuery<NxHubInsightsResponse>(
        ['nxhub-insights', params],
        async () => {
            const url = appendQueryParams('/api/nxhub/conversations/insights', {
                organizationId: params.organizationId,
                startMs: params.startMs,
                endMs: params.endMs,
                timeZoneId: params.timeZoneId,
                utcOffsetMinutes: params.utcOffsetMinutes,
            });
            return sendGet<NxHubInsightsResponse>(url);
        },
        {
            enabled,
            refetchOnWindowFocus: false,
            staleTime: 60 * 1000,
        }
    );
};

export const useGetNxHubConversation = (
    deviceSerial?: string,
    conversationId?: string,
    // Optional per-call overrides (e.g. refetchInterval to poll a freshly
    // completed conversation until its nxvetLabelId is written). The function
    // form receives the latest data so callers can stop polling once a
    // condition is met. Merged last so other callers keep the shared defaults.
    options?: { refetchInterval?: number | false | ((data: NxHubConversationItem | undefined) => number | false) },
) => {
    return useQuery<NxHubConversationItem>(
        ['nxhub-conversation', deviceSerial, conversationId],
        async () => sendGet<NxHubConversationItem>(`/api/nxhub/conversations/${deviceSerial}/${conversationId}`),
        {
            enabled: !!deviceSerial && !!conversationId,
            refetchOnWindowFocus: false,
            staleTime: 60 * 1000,
            refetchInterval: options?.refetchInterval,
        }
    );
};

// Full diarized transcript with segment-level timestamps.
// Backed by the .NET endpoint that streams the S3 transcript JSON.
export interface NxHubTranscriptSegment {
    startSec: number;
    endSec: number;
    speakerId: number;
    speakerLabel?: string;
    text: string;
}

export interface NxHubSpeakerTimelineEntry {
    speakerId: number;
    label: string;
    startSec: number;
    endSec: number;
}

export interface NxHubFullTranscript {
    deviceId: string;
    conversationId: string;
    text?: string;
    diarizedText?: string;
    segments: NxHubTranscriptSegment[];
    speakerTimeline: NxHubSpeakerTimelineEntry[];
    petData?: Record<string, any>;
    available: boolean;
}

export const useGetNxHubFullTranscript = (deviceSerial?: string, conversationId?: string) => {
    return useQuery<NxHubFullTranscript>(
        ['nxhub-full-transcript', deviceSerial, conversationId],
        async () => sendGet<NxHubFullTranscript>(`/api/nxhub/conversations/${deviceSerial}/${conversationId}/transcript`),
        {
            enabled: !!deviceSerial && !!conversationId,
            refetchOnWindowFocus: false,
            staleTime: 10 * 60 * 1000,
            retry: 1,
        }
    );
};

// Presigned URL + metadata for the conversation's playback audio.
// `source` distinguishes the canonical WAV (the exact bytes Whisper saw —
// always preferred for diarized playback) from a composed-from-minutes
// fallback that may drift by a few hundred ms relative to the transcript.
export interface NxHubConversationAudio {
    url: string;
    sizeBytes: number;
    durationSeconds: number;
    source: 'canonical' | 'composed';
}

export const useGetNxHubConversationAudio = (
    deviceSerial?: string,
    conversationId?: string,
    enabled: boolean = true,
) => {
    return useQuery<NxHubConversationAudio>(
        ['nxhub-conversation-audio', deviceSerial, conversationId],
        async () => sendGet<NxHubConversationAudio>(
            `/api/nxhub/conversations/${deviceSerial}/${conversationId}/audio`,
        ),
        {
            enabled: enabled && !!deviceSerial && !!conversationId,
            refetchOnWindowFocus: false,
            // Presigned URLs are short-lived; keep the cache bounded so a
            // reopened player gets a fresh streaming URL.
            staleTime: 10 * 60 * 1000,
            retry: 1,
        }
    );
};

// Server response for the force-complete endpoint. `conversation` is the
// freshly-updated item (status now "COMPLETED"); `message` notes whether the
// downstream processing message was published (still 200 if publish failed).
export interface CompleteNxHubConversationResponse {
    success: boolean;
    message?: string;
    conversation?: NxHubConversationItem;
}

// Force-complete an ACTIVE NxHUB recording: stops the live capture and hands
// the audio to the completer pipeline. The backend rejects anything that
// isn't ACTIVE (400) and uses optimistic locking (409 on a concurrent
// change). Invalidates both the list and the single-conversation cache so
// every surface (Soundbook, Devices page, detail page) reflects the new
// status without a manual refresh.
export const useCompleteNxHubConversation = () => {
    const queryClient = useQueryClient();
    return useMutation<CompleteNxHubConversationResponse, Error, { deviceId: string; conversationId: string }>(
        async ({ deviceId, conversationId }) =>
            sendPostForJson(
                `/api/nxhub/conversations/${encodeURIComponent(deviceId)}/${encodeURIComponent(conversationId)}/complete`,
            ),
        {
            onSuccess: (_, { deviceId, conversationId }) => {
                queryClient.invalidateQueries(['nxhub-conversations']);
                queryClient.invalidateQueries(['nxhub-conversation', deviceId, conversationId]);
            },
        }
    );
};

// Preview what a manual record over [startMs, endMs) for a device would
// contain, before creating it: how many audio minutes exist in the window and
// how much of it is speech. totalMinutes === 0 means there's no audio to record.
export interface PreviewNxHubConversationResponse {
    totalMinutes: number;
    speechMinutes: number;
    silentMinutes: number;
    totalSpeechDurationMs: number;
    startMs: number;
    endMs: number;
    deviceId: string;
    organizationId?: string;
    organizationName?: string;
}

export const usePreviewNxHubConversation = (
    params: { deviceId?: string; startMs?: number; endMs?: number },
    enabled: boolean = true,
) => {
    return useQuery<PreviewNxHubConversationResponse>(
        ['nxhub-conversation-preview', params.deviceId, params.startMs, params.endMs],
        async () => {
            const url = appendQueryParams('/api/nxhub/conversations/create/preview', {
                deviceId: params.deviceId,
                startMs: params.startMs,
                endMs: params.endMs,
            });
            return sendGet<PreviewNxHubConversationResponse>(url);
        },
        {
            enabled: enabled && !!params.deviceId && !!params.startMs && !!params.endMs,
            refetchOnWindowFocus: false,
            staleTime: 30 * 1000,
            retry: false,
        }
    );
};

// Manually create a record from a device + arbitrary time range: the backend
// composes the per-minute audio already in S3 and runs it through the completer
// pipeline (WAV + transcript + NxMIC label), no live conversation required.
// skipClinicalFilter=true so a window the user deliberately chose is never
// silently dropped as "non-clinical". Returns the new conversation (status
// COMPLETED), which the caller then polls for its nxvetLabelId.
export const useCreateNxHubConversation = () => {
    const queryClient = useQueryClient();
    return useMutation<
        CompleteNxHubConversationResponse,
        Error,
        { deviceId: string; startMs: number; endMs: number }
    >(
        async ({ deviceId, startMs, endMs }) =>
            sendPostForJson('/api/nxhub/conversations/create', {
                deviceId,
                startMs,
                endMs,
                skipClinicalFilter: true,
            }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['nxhub-conversations']);
            },
        }
    );
};
